import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LayoutDashboard, BarChart3, Image as ImageIcon, Layers, Shield, Calendar, Users, Repeat, Gift, UserPlus, History, Settings, Search, Plus, MoreVertical, ArrowUpRight, ArrowDownRight, Filter, Download, CheckCircle2, AlertCircle, KeyRound, Target, Crown, Edit3, Copy, Trash2, Sparkles, Video, MapPin, Flag, Eye, TrendingUp, Save, RotateCcw, ArrowRight, Map as MapIcon } from 'lucide-react';
interface TreasuryAdminDashboardProps {
  onClose: () => void;
  /**
   * When true, renders inline (no fixed overlay, no topbar) so the admin
   * dashboard slots into a parent container like the member dashboard preview.
   */
  inline?: boolean;
}
// ============================================================
// Mock data
// ============================================================
const ACTIVITY_30D = [18, 22, 25, 21, 28, 32, 30, 36, 41, 38, 44, 49, 46, 52, 58, 55, 61, 64, 60, 68, 72, 70, 75, 78, 74, 82, 86, 84, 91, 95];
const OPENRATE_14D = [62, 65, 64, 68, 71, 70, 74, 72, 75, 78, 77, 80, 82, 85];
const TOP_MISLS = [{
  name: 'Sukerchakia',
  category: 'Historic Prestige',
  members: 1520,
  xp: '480,210'
}, {
  name: 'Toronto Diaspora',
  category: 'Diaspora / City',
  members: 1042,
  xp: '312,840'
}, {
  name: 'Ahluwalia',
  category: 'Historic Prestige',
  members: 980,
  xp: '298,150'
}, {
  name: 'Majha Punjab',
  category: 'Homeland / Region',
  members: 845,
  xp: '241,300'
}, {
  name: 'Sandhu',
  category: 'Family / Clan',
  members: 712,
  xp: '198,720'
}];
const MISL_CATEGORIES: Record<string, {
  color: string;
  misls: {
    name: string;
    region: string;
    members: number;
    xp: string;
    misldar: string;
  }[];
}> = {
  'Historic Prestige': {
    color: '#D4AF37',
    misls: [{
      name: 'Sukerchakia',
      region: 'Gujranwala',
      members: 1520,
      xp: '480,210',
      misldar: '@ranjit_s'
    }, {
      name: 'Ahluwalia',
      region: 'Kapurthala',
      members: 980,
      xp: '298,150',
      misldar: '@jassa_a'
    }, {
      name: 'Bhangi',
      region: 'Lahore',
      members: 842,
      xp: '254,820',
      misldar: '@hari_b'
    }, {
      name: 'Kanhaiya',
      region: 'Sohian',
      members: 720,
      xp: '210,560',
      misldar: '@jai_k'
    }, {
      name: 'Ramgarhia',
      region: 'Amritsar',
      members: 695,
      xp: '198,440',
      misldar: '@nodh_r'
    }]
  },
  'Homeland / Region': {
    color: '#8B0000',
    misls: [{
      name: 'Majha Punjab',
      region: 'Amritsar Belt',
      members: 845,
      xp: '241,300',
      misldar: '@manjit_s'
    }, {
      name: 'Doaba Punjab',
      region: 'Jalandhar Belt',
      members: 730,
      xp: '208,910',
      misldar: '@parmjit_k'
    }, {
      name: 'Malwa Punjab',
      region: 'Patiala Belt',
      members: 680,
      xp: '189,420',
      misldar: '@balraj_s'
    }, {
      name: 'Pothohar',
      region: 'Rawalpindi',
      members: 412,
      xp: '112,380',
      misldar: '@gurnam_s'
    }, {
      name: 'Lahore Heartland',
      region: 'Lahore',
      members: 388,
      xp: '101,210',
      misldar: '@deep_l'
    }]
  },
  'Diaspora / City': {
    color: '#D4AF37',
    misls: [{
      name: 'Toronto Diaspora',
      region: 'GTA, Canada',
      members: 1042,
      xp: '312,840',
      misldar: '@harnoor_s'
    }, {
      name: 'Vancouver Diaspora',
      region: 'BC, Canada',
      members: 820,
      xp: '241,520',
      misldar: '@arvind_k'
    }, {
      name: 'London Diaspora',
      region: 'UK',
      members: 745,
      xp: '218,610',
      misldar: '@simran_b'
    }, {
      name: 'NYC Diaspora',
      region: 'New York, USA',
      members: 612,
      xp: '178,950',
      misldar: '@mehar_s'
    }, {
      name: 'Sydney Diaspora',
      region: 'NSW, Australia',
      members: 410,
      xp: '118,720',
      misldar: '@karam_s'
    }]
  },
  'Family / Clan': {
    color: '#8B0000',
    misls: [{
      name: 'Sandhu',
      region: 'Pan-Punjab',
      members: 712,
      xp: '198,720',
      misldar: '@gurdeep_s'
    }, {
      name: 'Sidhu',
      region: 'Malwa',
      members: 690,
      xp: '188,310',
      misldar: '@navjot_s'
    }, {
      name: 'Dhillon',
      region: 'Majha',
      members: 580,
      xp: '161,540',
      misldar: '@param_d'
    }, {
      name: 'Gill',
      region: 'Doaba & Malwa',
      members: 540,
      xp: '152,820',
      misldar: '@harman_g'
    }, {
      name: 'Brar',
      region: 'Faridkot Belt',
      members: 460,
      xp: '128,940',
      misldar: '@jaspal_b'
    }]
  }
};
const MEMBERS = [{
  name: 'Harnoor S.',
  handle: '@harnoor_s',
  rank: 'Sardar',
  misl: 'Sukerchakia',
  xp: '4,150',
  joined: 'Oct 12, 2023',
  status: 'Active'
}, {
  name: 'Ranjit S.',
  handle: '@ranjit_s',
  rank: 'Misldar',
  misl: 'Sukerchakia',
  xp: '12,400',
  joined: 'Sep 01, 2023',
  status: 'Active'
}, {
  name: 'Amrit K.',
  handle: '@amrit_k',
  rank: 'Courtier',
  misl: 'Ahluwalia',
  xp: '1,250',
  joined: 'Nov 05, 2023',
  status: 'Active'
}, {
  name: 'Dilraj S.',
  handle: '@dilraj_s',
  rank: 'Sardar',
  misl: 'Ramgarhia',
  xp: '6,910',
  joined: 'Sep 15, 2023',
  status: 'Active'
}, {
  name: 'Simran K.',
  handle: '@simran_k',
  rank: 'Sardar',
  misl: 'Toronto Diaspora',
  xp: '7,640',
  joined: 'Aug 22, 2023',
  status: 'Active'
}, {
  name: 'Jaspreet S.',
  handle: '@jas_s',
  rank: 'Courtier',
  misl: 'Sandhu',
  xp: '980',
  joined: 'Dec 02, 2023',
  status: 'Flagged'
}, {
  name: 'Manpreet K.',
  handle: '@manpreet_k',
  rank: 'Sardar',
  misl: 'London Diaspora',
  xp: '5,420',
  joined: 'Oct 28, 2023',
  status: 'Active'
}, {
  name: 'Arjan S.',
  handle: '@arjan_s',
  rank: 'Sardar',
  misl: 'Sukerchakia',
  xp: '8,150',
  joined: 'Sep 19, 2023',
  status: 'Active'
}];
const CODES = [{
  code: 'COURT-AWAKEN-7B',
  event: 'The Court Awakens — Opening Preview',
  xp: 200,
  used: 142,
  total: 200,
  expires: 'Jul 19, 2026',
  status: 'Active'
}, {
  code: 'SCENT-SONG-2C',
  event: 'The Chamber of Scent & Song',
  xp: 200,
  used: 0,
  total: 200,
  expires: 'Aug 16, 2026',
  status: 'Scheduled'
}, {
  code: 'AI-ARTISTS-9X',
  event: 'The AI Artists Behind the Faces',
  xp: 200,
  used: 38,
  total: 500,
  expires: 'Jul 26, 2026',
  status: 'Active'
}, {
  code: 'REGALIA-4K',
  event: 'The Royal Regalia Reveal',
  xp: 200,
  used: 0,
  total: 300,
  expires: 'Sep 20, 2026',
  status: 'Scheduled'
}, {
  code: 'FOUNDERS-FEAST-1',
  event: "Founders' Feast",
  xp: 500,
  used: 0,
  total: 100,
  expires: 'Oct 18, 2026',
  status: 'Scheduled'
}, {
  code: 'IMMERSIVE-BTS-3',
  event: 'Building the Darbar — Immersive Team',
  xp: 200,
  used: 21,
  total: 500,
  expires: 'Aug 30, 2026',
  status: 'Active'
}, {
  code: 'WELCOME-FOUNDER',
  event: 'Onboarding Welcome',
  xp: 100,
  used: 1820,
  total: 5000,
  expires: 'Dec 31, 2026',
  status: 'Active'
}, {
  code: 'PREVIEW-EXPIRED-1',
  event: 'Closed Court Preview (test)',
  xp: 100,
  used: 50,
  total: 50,
  expires: 'May 01, 2026',
  status: 'Expired'
}];
const RANJIT_IMG = 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png';
const JIND_IMG = 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png';
const NALWA_IMG = 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png';
const MORAN_IMG = 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png';
const ARTIFACTS = [{
  name: 'Maharaja Ranjit Singh',
  img: RANJIT_IMG,
  rarity: "Founders' Edition",
  set: 'Lahore Court',
  supply: 500,
  minted: 500,
  status: 'Live'
}, {
  name: 'Maharani Jind Kaur',
  img: JIND_IMG,
  rarity: 'Epic',
  set: 'Lahore Court',
  supply: 1000,
  minted: 450,
  status: 'Live'
}, {
  name: 'Hari Singh Nalwa',
  img: NALWA_IMG,
  rarity: 'Epic',
  set: 'Generals',
  supply: 1000,
  minted: 890,
  status: 'Live'
}, {
  name: 'Koh-i-Noor Diamond',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: 'Royal Treasury',
  supply: 150,
  minted: 120,
  status: 'Live'
}, {
  name: 'Golden Throne of Lahore',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: 'Royal Treasury',
  supply: 200,
  minted: 0,
  status: 'Draft'
}, {
  name: 'Royal Seal of Ranjit Singh',
  img: RANJIT_IMG,
  rarity: 'Treasury Rare',
  set: "Founders' Seals",
  supply: 500,
  minted: 412,
  status: 'Live'
}, {
  name: 'Akali Phula Singh',
  img: NALWA_IMG,
  rarity: 'Epic',
  set: 'Generals',
  supply: 1000,
  minted: 720,
  status: 'Live'
}, {
  name: 'Moran Sarkar',
  img: MORAN_IMG,
  rarity: 'Epic',
  set: 'Inner Life',
  supply: 1000,
  minted: 540,
  status: 'Live'
}, {
  name: 'Battle of Attock',
  img: NALWA_IMG,
  rarity: 'Treasury Rare',
  set: 'Defining Battles',
  supply: 250,
  minted: 0,
  status: 'Scheduled'
}, {
  name: 'Gurdwara Janam Asthan',
  img: RANJIT_IMG,
  rarity: 'Epic',
  set: 'Architecture',
  supply: 800,
  minted: 0,
  status: 'Draft'
}];
const COLLECTIONS = [{
  name: 'The Lahore Court',
  cards: 8,
  complete: 8,
  released: 'Mar 2024',
  status: 'Live',
  rarity: 'Mixed'
}, {
  name: "Founders' Seals",
  cards: 6,
  complete: 6,
  released: 'Mar 2024',
  status: 'Live',
  rarity: 'Treasury Rare'
}, {
  name: 'Generals of the Khalsa',
  cards: 10,
  complete: 9,
  released: 'May 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Royal Treasury',
  cards: 8,
  complete: 4,
  released: 'Jul 2024',
  status: 'Live',
  rarity: 'Treasury Rare'
}, {
  name: 'Inner Life of the Court',
  cards: 12,
  complete: 7,
  released: 'Sep 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Sons of the Sarkar',
  cards: 6,
  complete: 6,
  released: 'Nov 2024',
  status: 'Live',
  rarity: 'Epic'
}, {
  name: 'Defining Battles',
  cards: 10,
  complete: 0,
  released: 'Q3 2026',
  status: 'Scheduled',
  rarity: 'Treasury Rare'
}, {
  name: 'Architecture of the Empire',
  cards: 8,
  complete: 0,
  released: 'Q4 2026',
  status: 'Draft',
  rarity: 'Epic'
}];
const EVENTS_NIGHTS = [{
  date: 'Jul 18, 2026',
  title: 'The Court Awakens — Opening Preview',
  location: 'Illuminarium · Toronto',
  rank: 'All Ranks',
  code: 'COURT-AWAKEN-7B',
  attended: 142,
  capacity: 200,
  status: 'Active'
}, {
  date: 'Aug 15, 2026',
  title: 'The Chamber of Scent & Song',
  location: 'Illuminarium · Toronto',
  rank: 'All Ranks',
  code: 'SCENT-SONG-2C',
  attended: 0,
  capacity: 200,
  status: 'Scheduled'
}, {
  date: 'Sep 19, 2026',
  title: 'The Royal Regalia Reveal',
  location: 'Illuminarium · Toronto',
  rank: 'Courtier+',
  code: 'REGALIA-4K',
  attended: 0,
  capacity: 300,
  status: 'Scheduled'
}, {
  date: 'Oct 17, 2026',
  title: "Founders' Feast",
  location: 'Illuminarium · Toronto',
  rank: 'Sardar+',
  code: 'FOUNDERS-FEAST-1',
  attended: 0,
  capacity: 100,
  status: 'Scheduled'
}];
const EVENTS_SESSIONS = [{
  date: 'Jul 25, 2026',
  title: 'The AI Artists Behind the Faces',
  location: 'Virtual · Court Channel',
  rank: 'All Ranks',
  code: 'AI-ARTISTS-9X',
  attended: 38,
  capacity: 500,
  status: 'Active'
}, {
  date: 'Aug 29, 2026',
  title: 'Building the Darbar — Immersive Team',
  location: 'Virtual · Court Channel',
  rank: 'Courtier+',
  code: 'IMMERSIVE-BTS-3',
  attended: 21,
  capacity: 500,
  status: 'Active'
}, {
  date: 'Sep 26, 2026',
  title: 'The Sound of the Kingdom — Composing the Score',
  location: 'Virtual · Court Channel',
  rank: 'All Ranks',
  code: 'SOUND-KINGDOM-5',
  attended: 0,
  capacity: 500,
  status: 'Scheduled'
}, {
  date: 'Oct 24, 2026',
  title: 'Taste of Lahore — Royal Culinary Reveal',
  location: 'Virtual · Court Channel',
  rank: 'Sardar+',
  code: 'TASTE-LAHORE-8',
  attended: 0,
  capacity: 500,
  status: 'Scheduled'
}];
const TRADES_RECENT = [{
  id: 'TX-4821',
  from: '@ranjit_s',
  to: '@harnoor_s',
  artifact: 'Koh-i-Noor Diamond',
  value: '320 XP',
  fee: '1 XP',
  time: '2m ago',
  status: 'Completed'
}, {
  id: 'TX-4820',
  from: '@simran_k',
  to: '@arjan_s',
  artifact: 'Hari Singh Nalwa',
  value: '180 XP',
  fee: '1 XP',
  time: '14m ago',
  status: 'Completed'
}, {
  id: 'TX-4819',
  from: '@amrit_k',
  to: '@dilraj_s',
  artifact: 'Royal Seal of Ranjit Singh',
  value: '410 XP',
  fee: '1 XP',
  time: '48m ago',
  status: 'Completed'
}, {
  id: 'TX-4818',
  from: '@jas_s',
  to: '@manpreet_k',
  artifact: 'Maharani Jind Kaur',
  value: '240 XP',
  fee: '1 XP',
  time: '1h ago',
  status: 'Completed'
}];
const TRADES_FLAGGED = [{
  id: 'TX-4817',
  from: '@jas_s',
  to: '@new_user_x91',
  artifact: 'Maharaja Ranjit Singh',
  value: '5 XP',
  fee: '1 XP',
  time: '3h ago',
  reason: 'Suspicious price · same IP',
  status: 'Flagged'
}, {
  id: 'TX-4810',
  from: '@bot_acc_22',
  to: '@jas_s',
  artifact: 'Akali Phula Singh',
  value: '10 XP',
  fee: '1 XP',
  time: '6h ago',
  reason: 'Possible bot account',
  status: 'Flagged'
}, {
  id: 'TX-4805',
  from: '@new_user_x91',
  to: '@jas_s',
  artifact: 'Moran Sarkar',
  value: '8 XP',
  fee: '1 XP',
  time: '9h ago',
  reason: 'Reciprocal low-value trade',
  status: 'Flagged'
}];
const QUESTS = [{
  name: 'First Light',
  trigger: 'Open first Daily Packet',
  reward: '+100 XP',
  cadence: 'One-shot',
  completion: 96,
  status: 'Active'
}, {
  name: 'Daily Devotion',
  trigger: 'Open Daily Packet (7 days in a row)',
  reward: '+250 XP · Bonus Packet',
  cadence: 'Recurring',
  completion: 42,
  status: 'Active'
}, {
  name: 'The Trader',
  trigger: 'Complete 3 trades',
  reward: '+150 XP',
  cadence: 'Recurring',
  completion: 28,
  status: 'Active'
}, {
  name: 'Court Witness',
  trigger: 'Attend a Treasury Night',
  reward: '+200 XP · Live Attended Badge',
  cadence: 'One-shot',
  completion: 18,
  status: 'Active'
}, {
  name: "Founder's Decree",
  trigger: 'Invite 5 new members',
  reward: 'Founder Seal · +500 XP',
  cadence: 'One-shot',
  completion: 8,
  status: 'Active'
}, {
  name: 'Set Sealer',
  trigger: 'Complete any 6-card collection',
  reward: '+300 XP',
  cadence: 'Recurring',
  completion: 22,
  status: 'Active'
}, {
  name: 'The Pilgrim',
  trigger: 'Attend 3 Court Sessions',
  reward: '+400 XP · Pilgrim Sash',
  cadence: 'One-shot',
  completion: 4,
  status: 'Draft'
}];
const REFERRERS = [{
  rank: 1,
  name: 'Harnoor S.',
  handle: '@harnoor_s',
  invites: 47,
  signups: 32,
  xp: '+8,400'
}, {
  rank: 2,
  name: 'Ranjit S.',
  handle: '@ranjit_s',
  invites: 41,
  signups: 29,
  xp: '+7,250'
}, {
  rank: 3,
  name: 'Simran K.',
  handle: '@simran_k',
  invites: 38,
  signups: 24,
  xp: '+6,000'
}, {
  rank: 4,
  name: 'Dilraj S.',
  handle: '@dilraj_s',
  invites: 31,
  signups: 19,
  xp: '+4,750'
}, {
  rank: 5,
  name: 'Amrit K.',
  handle: '@amrit_k',
  invites: 28,
  signups: 17,
  xp: '+4,250'
}, {
  rank: 6,
  name: 'Manpreet K.',
  handle: '@manpreet_k',
  invites: 24,
  signups: 14,
  xp: '+3,500'
}, {
  rank: 7,
  name: 'Arjan S.',
  handle: '@arjan_s',
  invites: 21,
  signups: 12,
  xp: '+3,000'
}];
const AUDIT_LOG = [{
  ts: '2026-05-13 09:42:14',
  admin: '@harnoor_s',
  action: 'granted +500 XP to',
  target: '@harjeet_k',
  kind: 'XP Grant'
}, {
  ts: '2026-05-13 09:18:02',
  admin: '@harnoor_s',
  action: 'created event',
  target: "Founders' Feast",
  kind: 'Event'
}, {
  ts: '2026-05-13 08:51:30',
  admin: '@ranjit_s',
  action: 'archived artifact',
  target: 'Test Card v0.3',
  kind: 'Artifact'
}, {
  ts: '2026-05-12 22:04:11',
  admin: '@harnoor_s',
  action: 'set Misldar of Bhangi Misl to',
  target: '@hari_b',
  kind: 'Misl'
}, {
  ts: '2026-05-12 18:30:55',
  admin: '@ranjit_s',
  action: 'flagged trade',
  target: 'TX-4817',
  kind: 'Trade'
}, {
  ts: '2026-05-12 14:20:08',
  admin: '@harnoor_s',
  action: 'generated code',
  target: 'FOUNDERS-FEAST-1',
  kind: 'Code'
}, {
  ts: '2026-05-12 11:15:42',
  admin: '@harnoor_s',
  action: 'updated packet drop weights',
  target: 'Founders 1% / Rare 8%',
  kind: 'Settings'
}, {
  ts: '2026-05-11 20:08:19',
  admin: '@ranjit_s',
  action: 'banned account',
  target: '@bot_acc_22',
  kind: 'Member'
}, {
  ts: '2026-05-11 16:42:31',
  admin: '@harnoor_s',
  action: 'launched collection',
  target: 'Sons of the Sarkar',
  kind: 'Collection'
}, {
  ts: '2026-05-11 12:01:55',
  admin: '@harnoor_s',
  action: 'updated brand name to',
  target: 'Treasures of the Sikh Empire',
  kind: 'Settings'
}];
const TIER_REWARDS = [{
  count: 1,
  reward: '+500 XP',
  label: 'First invite bonus'
}, {
  count: 3,
  reward: 'Bonus Packet',
  label: 'A free Treasury packet'
}, {
  count: 5,
  reward: 'Founder Seal',
  label: 'A permanent identity seal'
}, {
  count: 10,
  reward: 'Live Night ticket',
  label: 'Reserved seat at Treasury Nights'
}];
// ============================================================
// Visual helpers
// ============================================================
const SectionHeader = ({
  title,
  eyebrow,
  action
}: {
  title: string;
  eyebrow: string;
  action?: React.ReactNode;
}) => <div className="flex flex-wrap gap-4 justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-2406" data-mp="cvkr5i87d02z">
    <div data-id="element-2407" data-mp="1xhskv827m9cy">
      <h2 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-2408" data-mp="1sl23mheo57py">{title}</h2>
      <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-2409" data-mp="1sksf6y99v8md">
        {eyebrow}
      </p>
    </div>
    {action && <div className="flex flex-wrap gap-3 items-center" data-id="element-2410" data-mp="22b57jcnbw632">{action}</div>}
  </div>;
const StatusPill = ({
  status
}: {
  status: string;
}) => {
  const map: Record<string, string> = {
    Active: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Live: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Completed: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5',
    Scheduled: 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5',
    Draft: 'text-[#FDFBF7]/50 border-[#FDFBF7]/20 bg-[#FDFBF7]/5',
    Expired: 'text-[#FDFBF7]/35 border-[#FDFBF7]/15 bg-[#FDFBF7]/[0.02]',
    Flagged: 'text-red-400 border-red-400/40 bg-red-400/5',
    Banned: 'text-red-400 border-red-400/40 bg-red-400/5'
  };
  const cls = map[status] ?? 'text-[#FDFBF7]/60 border-[#FDFBF7]/20 bg-[#FDFBF7]/5';
  return <span className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-widest border px-2 py-1 ${cls}`} data-id="element-2411" data-mp="3oppkgwvocgvh">
      {(status === 'Active' || status === 'Live' || status === 'Completed') && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2412" data-mp="1j6lculjyo0j" />}
      {status}
    </span>;
};
const RarityChip = ({
  rarity
}: {
  rarity: string;
}) => {
  let cls = 'border-[#FDFBF7]/20 text-[#FDFBF7]/60 bg-[#FDFBF7]/5';
  if (rarity.includes('Founders')) cls = 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10';else if (rarity.includes('Rare')) cls = 'border-[#D4AF37]/50 text-[#D4AF37]/80 bg-[#D4AF37]/5';else if (rarity.includes('Epic')) cls = 'border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10';
  return <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border whitespace-nowrap ${cls}`} data-id="element-2413" data-mp="z5dri8xi8of0">
      {rarity}
    </span>;
};
const KpiTile = ({
  label,
  value,
  trend,
  up
}: {
  label: string;
  value: string;
  trend?: string;
  up?: boolean;
}) => <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2414" data-mp="1it08el4xzrlu">
    <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest mb-2" data-id="element-2415" data-mp="13x11hdtkjxia">
      {label}
    </p>
    <div className="flex items-end justify-between" data-id="element-2416" data-mp="18ujcd9r5cmsx">
      <p className="font-serif text-2xl lg:text-3xl text-[#D4AF37]" data-id="element-2417" data-mp="18x5ir62aww9k">{value}</p>
      {trend && <p className={`text-[10px] flex items-center gap-1 ${up ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-2418" data-mp="v235tyz1e6n9">
          {up ? <ArrowUpRight className="w-3 h-3" data-id="element-2419" data-mp="l6jdtnnlfs3s" /> : <ArrowDownRight className="w-3 h-3" data-id="element-2420" data-mp="q4umhv25jwkf" />}
          {trend}
        </p>}
    </div>
  </div>;
// SVG line chart
const ActivityChart = ({
  data,
  height = 180
}: {
  data: number[];
  height?: number;
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const width = 800;
  const padding = 24;
  const stepX = (width - padding * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = padding + i * stepX;
    const y = padding + (1 - (v - min) / (max - min || 1)) * (height - padding * 2);
    return [x, y] as [number, number];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1][0]} ${height - padding} L ${points[0][0]} ${height - padding} Z`;
  return <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{
    height: `${height}px`
  }} preserveAspectRatio="none" data-id="element-2421" data-mp="2wtwo0rgr123e">
      <defs data-id="element-2422" data-mp="2nrnbjw1x6ynd">
        <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-2423" data-mp="2nrvo1mu4s1y1">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" data-id="element-2424" data-mp="1gf3or0l9xp81" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" data-id="element-2425" data-mp="1cd9tj7tuldf9" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map(g => <line key={g} x1={padding} x2={width - padding} y1={padding + g * (height - padding * 2)} y2={padding + g * (height - padding * 2)} stroke="#D4AF37" strokeOpacity="0.08" strokeDasharray="3 5" data-id="element-2426" data-mp="3o1wra6w8cd79" />)}
      <path d={areaPath} fill="url(#area-grad)" data-id="element-2427" data-mp="5nnr2a57auqq" />
      <path d={linePath} fill="none" stroke="#D4AF37" strokeWidth="1.5" data-id="element-2428" data-mp="alpcv2u0h85w" />
      {points.map(([x, y], i) => i === points.length - 1 ? <circle key={i} cx={x} cy={y} r="3" fill="#D4AF37" data-id="element-2429" data-mp="fjqs1cobgnc3" /> : null)}
    </svg>;
};
const Toggle = ({
  on,
  onChange
}: {
  on: boolean;
  onChange: () => void;
}) => <button type="button" onClick={onChange} className={`relative w-10 h-5 transition-colors border ${on ? 'bg-[#D4AF37]/30 border-[#D4AF37]' : 'bg-[#0B0F19] border-[#D4AF37]/20'}`} aria-pressed={on} data-id="element-2430" data-mp="om0446z8gorl">
    <span className={`absolute top-0.5 w-3.5 h-3.5 transition-transform ${on ? 'translate-x-5 bg-[#D4AF37]' : 'translate-x-0.5 bg-[#FDFBF7]/40'}`} data-id="element-2431" data-mp="2qdwe3c5r5qk9" />
  </button>;
// ============================================================
// Main component
// ============================================================
export const TreasuryAdminDashboard: React.FC<TreasuryAdminDashboardProps> = ({
  onClose,
  inline = false
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [memberRankFilter, setMemberRankFilter] = useState('All');
  const [memberMislFilter, setMemberMislFilter] = useState('All');
  const [artifactRarityFilter, setArtifactRarityFilter] = useState('All');
  const [eventSubTab, setEventSubTab] = useState<'nights' | 'sessions'>('nights');
  const [tradeSubTab, setTradeSubTab] = useState<'recent' | 'flagged'>('recent');
  const [dropWeights, setDropWeights] = useState({
    founders: 1,
    rare: 8,
    epic: 22,
    standard: 69
  });
  const [featureFlags, setFeatureFlags] = useState({
    tradeMarket: true,
    courtSessions: true,
    mislSwitching: false,
    publicLeaderboard: true
  });
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'members',
    label: 'Members',
    icon: Users
  }, {
    id: 'misls',
    label: 'Misls',
    icon: Shield
  }, {
    id: 'codes',
    label: 'Codes',
    icon: KeyRound
  }, {
    id: 'collectibles',
    label: 'Collectibles',
    icon: ImageIcon
  }, {
    id: 'collections',
    label: 'Collections',
    icon: Layers
  }, {
    id: 'events',
    label: 'Live Events',
    icon: Calendar
  }, {
    id: 'trades',
    label: 'Trades',
    icon: Repeat
  }, {
    id: 'packets',
    label: 'Daily Packet',
    icon: Gift
  }, {
    id: 'restoration',
    label: 'Restoration',
    icon: MapIcon
  }, {
    id: 'quests',
    label: 'Quests',
    icon: Target
  }, {
    id: 'referrals',
    label: 'Referrals',
    icon: UserPlus
  }, {
    id: 'audit',
    label: 'Audit Log',
    icon: History
  }, {
    id: 'settings',
    label: 'Settings',
    icon: Settings
  }];
  const allMisls = Object.values(MISL_CATEGORIES).flatMap(c => c.misls.map(m => m.name));
  const ranks = ['Mildar', 'Misldar', 'Sardar', 'Courtier', 'Initiate'];
  const rarities = ["Founders' Edition", 'Treasury Rare', 'Epic'];
  const filteredMembers = MEMBERS.filter(m => {
    if (memberRankFilter !== 'All' && m.rank !== memberRankFilter) return false;
    if (memberMislFilter !== 'All' && m.misl !== memberMislFilter) return false;
    return true;
  });
  const filteredArtifacts = ARTIFACTS.filter(a => artifactRarityFilter === 'All' || a.rarity === artifactRarityFilter);
  const dropTotal = dropWeights.founders + dropWeights.rare + dropWeights.epic + dropWeights.standard;
  const renderContent = () => {
    switch (activeTab) {
      // ============================================================
      // OVERVIEW
      // ============================================================
      case 'overview':
        return <div className="space-y-6" data-id="element-2432" data-mp="1n2sajato51wx">
            <SectionHeader title="System Overview" eyebrow="Real-time Treasury Metrics" action={<button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2434" data-mp="vcdzrngyc5ph">
                  <Download className="w-3.5 h-3.5" data-id="element-2435" data-mp="14ctyykqnwqoj" /> Export Report
                </button>} data-id="element-2433" data-mp="1n3tslz93qoo5" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2436" data-mp="19bx5yaq7d6z5">
              <KpiTile label="Total Members" value="12,450" trend="+145 wk" up data-id="element-2437" data-mp="2frpoj1qukcjv" />
              <KpiTile label="Active (24h)" value="3,820" trend="+12%" up data-id="element-2438" data-mp="2kp93y4za6vde" />
              <KpiTile label="Packets Today" value="2,140" trend="+8%" up data-id="element-2439" data-mp="35vwdxh6ggwot" />
              <KpiTile label="Trade Volume 7d" value="1,240" trend="-5%" up={false} data-id="element-2440" data-mp="31tj4oisi2gna" />
              <KpiTile label="Total XP Issued" value="4.2M" trend="+220K" up data-id="element-2441" data-mp="3fmr1qpzco4cd" />
              <KpiTile label="Codes Redeemed" value="2,071" trend="+142" up data-id="element-2442" data-mp="3asefy9aeigxg" />
              <KpiTile label="Active Misls" value="20 / 20" trend="All live" up data-id="element-2443" data-mp="3phspbhdz8ycu" />
              <KpiTile label="Flagged Trades" value="3" trend="Review" up={false} data-id="element-2444" data-mp="2e5qvb7388a1" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2445" data-mp="3tick4eb2vpvt">
              <div className="flex items-end justify-between mb-4" data-id="element-2446" data-mp="24610ll9sa2pm">
                <div data-id="element-2447" data-mp="1z77ljoeum3nc">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2448" data-mp="1z7pp763ubk5a">
                    30-Day Activity
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2449" data-mp="eqq1mq1mzvjm">
                    Daily Active Members
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-2450" data-mp="5tz7ij6naqro">
                  {['7D', '30D', '90D', 'All'].map(p => <button key={p} className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border ${p === '30D' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70'}`} data-id="element-2451" data-mp="9vkifyzmcuz8">
                      {p}
                    </button>)}
                </div>
              </div>
              <ActivityChart data={ACTIVITY_30D} data-id="element-2452" data-mp="1hbi4httmtxhs" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2453" data-mp="1h94c5sqc6zdx">
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2454" data-mp="1lbiv0cu09z57">
                <div className="flex items-center justify-between mb-4" data-id="element-2455" data-mp="2o30viubwikad">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2456" data-mp="2t0sug8ayr6o2">
                    Top Misls
                  </h3>
                  <button onClick={() => setActiveTab('misls')} className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2457" data-mp="2x1uo89m42qin">
                    Manage All →
                  </button>
                </div>
                <div className="divide-y divide-[#D4AF37]/10" data-id="element-2458" data-mp="3bqh9eryf6chs">
                  {TOP_MISLS.map((m, i) => <div key={m.name} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0" data-id="element-2459" data-mp="3gr52dj67l3en" data-mpi={i}>
                      <span className="font-mono text-[#D4AF37]/70 text-sm w-6" data-id="element-2460" data-mp="1md47tahoz2kd">
                        #{i + 1}
                      </span>
                      <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center shrink-0" data-id="element-2461" data-mp="1qehchh91szpp">
                        <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2462" data-mp="1e3yfsu2gu9rx" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2463" data-mp="194v9l6ar1xid">
                        <p className="text-[#FDFBF7] text-sm leading-tight truncate" data-id="element-2464" data-mp="195ep6gcxg9t8">
                          {m.name}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2465" data-mp="21talhjyl3zph">
                          {m.category}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-2466" data-mp="1ssm5ijpmrp9y">
                        <p className="text-[#D4AF37] text-xs font-mono" data-id="element-2467" data-mp="1srb0yhod2dhs">
                          {m.xp} XP
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-2468" data-mp="hdpej49vypzy">
                          {m.members.toLocaleString()} members
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2469" data-mp="iuuviot7awl9">
                <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2470" data-mp="iss2dvwr4ncb">
                  System Health
                </h3>
                <div className="space-y-3.5" data-id="element-2471" data-mp="8wezc7fbgx4w">
                  {[{
                  k: 'API Status',
                  v: 'Operational',
                  tone: 'emerald'
                }, {
                  k: 'Packet RNG Integrity',
                  v: '✓ Verified',
                  tone: 'emerald'
                }, {
                  k: 'Database Load',
                  v: '24%',
                  tone: 'gold'
                }, {
                  k: 'Active Events',
                  v: '2 Live',
                  tone: 'gold'
                }, {
                  k: 'Trade Queue',
                  v: '0 backlog',
                  tone: 'emerald'
                }, {
                  k: 'Flagged Trades',
                  v: '3 Pending',
                  tone: 'red'
                }].map((row, i) => <div key={i} className="flex items-center justify-between" data-id="element-2472" data-mp="2yqkitkvzv0y5" data-mpi={i}>
                      <span className="text-[#FDFBF7]/60 text-xs" data-id="element-2473" data-mp="3cm4qtla6pyqs">{row.k}</span>
                      <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${row.tone === 'emerald' ? 'text-emerald-400' : row.tone === 'red' ? 'text-red-400' : 'text-[#D4AF37]'}`} data-id="element-2474" data-mp="37o3851go81fu">
                        {row.tone === 'emerald' && <CheckCircle2 className="w-3 h-3" data-id="element-2475" data-mp="3veqgtoyeuv3g" />}
                        {row.tone === 'red' && <AlertCircle className="w-3 h-3" data-id="element-2476" data-mp="1oq832pw5b86f" />}
                        {row.v}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2477" data-mp="1yhb2zodc9h44">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2478" data-mp="22huykni9kco5">
                Live Activity Stream
              </h3>
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-2479" data-mp="15sx0avbb1wm8">
                {[{
                user: '@ranjit_s',
                action: 'opened a Daily Packet · pulled Royal Seal',
                time: '2m ago',
                type: 'packet'
              }, {
                user: '@harjeet_k',
                action: 'completed Founders Set (6/6)',
                time: '15m ago',
                type: 'collection'
              }, {
                user: '@simran_k',
                action: 'redeemed code COURT-AWAKEN-7B (+200 XP)',
                time: '38m ago',
                type: 'code'
              }, {
                user: '@arjan_s',
                action: 'joined Sukerchakia Misl',
                time: '1h ago',
                type: 'misl'
              }, {
                user: '@dilraj_s',
                action: 'traded Koh-i-Noor Diamond for 3 cards',
                time: '2h ago',
                type: 'trade'
              }].map((act, i) => <div key={i} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-2480" data-mp="2cltiymgwnjl3" data-mpi={i}>
                    <div className="flex items-center gap-3" data-id="element-2481" data-mp="augg02yhzlro">
                      <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-2482" data-mp="6slfh0oe5cey">
                        {act.type === 'packet' && <Gift className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2483" data-mp="209hcib4extb3" />}
                        {act.type === 'collection' && <Layers className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2484" data-mp="1rbnhr4mar7q1" />}
                        {act.type === 'trade' && <Repeat className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2485" data-mp="2j1sbhtvy573x" />}
                        {act.type === 'misl' && <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2486" data-mp="2nzlhaltc7z3o" />}
                        {act.type === 'code' && <KeyRound className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2487" data-mp="2f06p9hxivgh2" />}
                      </div>
                      <p className="text-sm text-[#FDFBF7]/80" data-id="element-2488" data-mp="yloxrnmym0s8">
                        <span className="text-[#D4AF37] font-medium" data-id="element-2489" data-mp="13l1l09hqt9vc">
                          {act.user}
                        </span>{' '}
                        {act.action}
                      </p>
                    </div>
                    <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest shrink-0" data-id="element-2490" data-mp="r379gbafchf7">
                      {act.time}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // MEMBERS
      // ============================================================
      case 'members':
        return <div className="space-y-6" data-id="element-2491" data-mp="7clsp58mb2to">
            <SectionHeader title="Member Directory" eyebrow={`${MEMBERS.length} members · ${filteredMembers.length} shown`} action={<>
                  <div className="relative" data-id="element-2493" data-mp="3prnza1up18rt">
                    <Search className="w-4 h-4 text-[#D4AF37]/50 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2494" data-mp="3pqcupztfbwzn" />
                    <input type="text" placeholder="Search by handle or email..." className="pl-9 pr-4 py-2 bg-[#131B2B] border border-[#D4AF37]/30 text-[#FDFBF7] text-xs focus:outline-none focus:border-[#D4AF37] w-64" data-id="element-2495" data-mp="3krie94ixikip" />
                  </div>
                  <button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2496" data-mp="3e7tvt6hpe57g">
                    <Plus className="w-3.5 h-3.5" data-id="element-2497" data-mp="3n7pavmedqkcr" /> Invite Member
                  </button>
                </>} data-id="element-2492" data-mp="m3m9pynfk51f" />

            <div className="flex flex-wrap gap-3 items-center bg-[#131B2B] border border-[#D4AF37]/20 p-4" data-id="element-2498" data-mp="3s00bkrgy2a6d">
              <span className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-2499" data-mp="ruunvfis7ht">
                <Filter className="w-3.5 h-3.5" data-id="element-2500" data-mp="5tbsfi2pjdu6" /> Filter
              </span>
              <select value={memberRankFilter} onChange={e => setMemberRankFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2501" data-mp="9tvrwsar6tjh">
                <option value="All" data-id="element-2502" data-mp="1lbhw9nz16f78">All Ranks</option>
                {ranks.map(r => <option key={r} value={r} data-id="element-2503" data-mp="1gcdmav4a8dfu">
                    {r}
                  </option>)}
              </select>
              <select value={memberMislFilter} onChange={e => setMemberMislFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2504" data-mp="2dyb1aijge0n1">
                <option value="All" data-id="element-2505" data-mp="1z9xzh4xopnkm">All Misls</option>
                {allMisls.map(m => <option key={m} value={m} data-id="element-2506" data-mp="2wr6jmgg75g8k">
                    {m}
                  </option>)}
              </select>
              {(memberRankFilter !== 'All' || memberMislFilter !== 'All') && <button onClick={() => {
              setMemberRankFilter('All');
              setMemberMislFilter('All');
            }} className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2507" data-mp="sq3pi1gqsrxs">
                  Clear filters
                </button>}
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2508" data-mp="91cx6jpf1nl3">
              <table className="w-full text-left border-collapse" data-id="element-2509" data-mp="4wveyoypficf">
                <thead data-id="element-2510" data-mp="3w3y5rfamrmm4">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2511" data-mp="3w59k291pccrh">
                    <th className="p-4 font-normal" data-id="element-2512" data-mp="3r7r96d44jngu">Member</th>
                    <th className="p-4 font-normal" data-id="element-2513" data-mp="1ucbmjzwzcw07">Rank</th>
                    <th className="p-4 font-normal" data-id="element-2514" data-mp="1pea0d649cgki">Misl</th>
                    <th className="p-4 font-normal" data-id="element-2515" data-mp="1pf31nrkg6dv9">XP</th>
                    <th className="p-4 font-normal" data-id="element-2516" data-mp="1wykp3g0tbnx3">Status</th>
                    <th className="p-4 font-normal" data-id="element-2517" data-mp="1wvygre63l4dt">Joined</th>
                    <th className="p-4 font-normal text-right" data-id="element-2518" data-mp="21vksfj6a2cki">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2519" data-mp="26odws5xu3iw2">
                  {filteredMembers.map(user => <tr key={user.handle} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2520" data-mp="2bojnnjwa51d3">
                      <td className="p-4" data-id="element-2521" data-mp="1iyjom1ogmii5">
                        <div className="flex items-center gap-3" data-id="element-2522" data-mp="1nx4fhahtnptx">
                          <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-2523" data-mp="1nvt8z2wzs3z4">
                            <span className="text-[#D4AF37] text-xs font-serif" data-id="element-2524" data-mp="2zcjkn1k6po04">
                              {user.name.charAt(0)}
                            </span>
                          </div>
                          <div data-id="element-2525" data-mp="148uzkh71wiqb">
                            <p className="text-[#FDFBF7] text-sm font-medium leading-tight" data-id="element-2526" data-mp="14823qb9mrdmy">
                              {user.name}
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5" data-id="element-2527" data-mp="1d9jdlbwg2j37">
                              {user.handle}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs" data-id="element-2528" data-mp="lj59g7e8gl4q">
                        {user.rank}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2529" data-mp="ghpnmd48eala">
                        {user.misl}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2530" data-mp="pjytuqsgvhsv">
                        {user.xp}
                      </td>
                      <td className="p-4" data-id="element-2531" data-mp="1stfkg26cxko">
                        <StatusPill status={user.status} data-id="element-2532" data-mp="3tyou4i0u8v86" />
                      </td>
                      <td className="p-4 text-[#FDFBF7]/50 text-xs" data-id="element-2533" data-mp="3jui9bqn9gbgx">
                        {user.joined}
                      </td>
                      <td className="p-4 text-right" data-id="element-2534" data-mp="3olgouo0fd5j4">
                        <div className="flex gap-2 justify-end" data-id="element-2535" data-mp="3on1mwnly76lq">
                          <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2536" data-mp="39xnebhq74t7e">
                            Grant XP
                          </button>
                          <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[9px] uppercase tracking-widest border border-[#FDFBF7]/20 px-2 py-1 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2537" data-mp="beyiszdz575k">
                            Promote
                          </button>
                          <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2538" data-mp="2h7n37153qjw3">
                            <MoreVertical className="w-4 h-4" data-id="element-2539" data-mp="2m6hxp0cflww1" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                  {filteredMembers.length === 0 && <tr data-id="element-2540" data-mp="2xll92y0wycoq">
                      <td colSpan={7} className="p-12 text-center text-[#FDFBF7]/40 text-sm" data-id="element-2541" data-mp="2xlbfl0gntnea">
                        No members match the current filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // MISLS
      // ============================================================
      case 'misls':
        return <div className="space-y-6" data-id="element-2542" data-mp="36eybcqkvaqnh">
            <SectionHeader title="Misl Management" eyebrow="20 Misls across 4 categories" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2544" data-mp="1uzjdlkku3urb">
                  <Plus className="w-3.5 h-3.5" data-id="element-2545" data-mp="fienxs0tpw07" /> Add New Misl
                </button>} data-id="element-2543" data-mp="1zzdzf9pr5u5i" />

            {Object.entries(MISL_CATEGORIES).map(([category, data]) => <div key={category} className="space-y-3" data-id="element-2546" data-mp="p7g2revmu7cl">
                <div className="flex items-center justify-between border-l-2 pl-4" style={{
              borderColor: data.color
            }} data-id="element-2547" data-mp="p8zjf6l7akew">
                  <div data-id="element-2548" data-mp="136xuo34860q6">
                    <h3 className="font-serif text-xl text-[#FDFBF7] leading-tight" data-id="element-2549" data-mp="136o4ofj6jtkh">
                      {category}
                    </h3>
                    <p className="text-[10px] tracking-[0.3em] uppercase mt-1" style={{
                  color: data.color + 'cc'
                }} data-id="element-2550" data-mp="18594iduisb8e">
                      {data.misls.length} Misls ·{' '}
                      {data.misls.reduce((s, m) => s + m.members, 0).toLocaleString()}{' '}
                      members
                    </p>
                  </div>
                </div>

                <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2551" data-mp="36vsj3zr5k2ae">
                  <table className="w-full text-left border-collapse" data-id="element-2552" data-mp="36y5bl6ujdv1p">
                    <thead data-id="element-2553" data-mp="3ku0omw298bx6">
                      <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2554" data-mp="3kt7erm8016at">
                        <th className="p-4 font-normal" data-id="element-2555" data-mp="3ulwly4winy0c">Misl</th>
                        <th className="p-4 font-normal" data-id="element-2556" data-mp="3unhejoz9t6vk">Region</th>
                        <th className="p-4 font-normal" data-id="element-2557" data-mp="3pnlajpqkpfg0">Members</th>
                        <th className="p-4 font-normal" data-id="element-2558" data-mp="8dnbrfv6c0v1">Misl XP</th>
                        <th className="p-4 font-normal" data-id="element-2559" data-mp="8bsnbrkp1qm7">Misldar</th>
                        <th className="p-4 font-normal text-right" data-id="element-2560" data-mp="3gn2yx6xwk08">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2561" data-mp="mz15jccf6w7o">
                      {data.misls.map(m => <tr key={m.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2562" data-mp="d2y1u21x6pim">
                          <td className="p-4" data-id="element-2563" data-mp="i3vj8cf3vfj2">
                            <div className="flex items-center gap-3" data-id="element-2564" data-mp="3cml1ex50lub">
                              <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2565" data-mp="83l8qzkggm7l">
                                <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2566" data-mp="3qido659r2eyg" />
                              </div>
                              <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2567" data-mp="3vhgstoltij5w">
                                {m.name}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2568" data-mp="3lkltx4lmugr3">
                            {m.region}
                          </td>
                          <td className="p-4 text-[#FDFBF7]/80 text-xs font-mono" data-id="element-2569" data-mp="t02u6y0dk3e">
                            {m.members.toLocaleString()}
                          </td>
                          <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2570" data-mp="3s1nji9lb0j4m">
                            {m.xp}
                          </td>
                          <td className="p-4" data-id="element-2571" data-mp="3i2olrm22u7we">
                            <div className="flex items-center gap-2" data-id="element-2572" data-mp="3i3hng8m6i77o">
                              <Crown className="w-3 h-3 text-[#D4AF37]" data-id="element-2573" data-mp="3d9dg3o903x62" />
                              <span className="text-[#D4AF37] text-[10px] font-mono" data-id="element-2574" data-mp="39aeby5v3g4fo">
                                {m.misldar}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right" data-id="element-2575" data-mp="2zadoc7q6z25o">
                            <div className="flex gap-2 justify-end" data-id="element-2576" data-mp="2ubsmy4z7ecfn">
                              <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-1.5" data-id="element-2577" data-mp="2k85kuwj3ol3h">
                                <Crown className="w-3 h-3" data-id="element-2578" data-mp="2u61u1ish95yy" /> Set Misldar
                              </button>
                              <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[9px] uppercase tracking-widest border border-[#FDFBF7]/20 px-2 py-1 hover:bg-[#FDFBF7]/5 transition-colors flex items-center gap-1.5" data-id="element-2579" data-mp="2z4d33ray7cg4">
                                <Edit3 className="w-3 h-3" data-id="element-2580" data-mp="26h0ihmoclepg" /> Edit
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>)}
          </div>;
      // ============================================================
      // CODES
      // ============================================================
      case 'codes':
        return <div className="space-y-6" data-id="element-2581" data-mp="10zgb1vgsxj04">
            <SectionHeader title="Redemption Code Registry" eyebrow={`${CODES.length} codes · ${CODES.reduce((s, c) => s + c.used, 0).toLocaleString()} redemptions logged`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2583" data-mp="r2to914x0hvg">
                  <Plus className="w-3.5 h-3.5" data-id="element-2584" data-mp="i1di55l4v1x1" /> Generate Code
                </button>} data-id="element-2582" data-mp="10yemqparz00g" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2585" data-mp="39bk7r2miizn">
              <KpiTile label="Active Codes" value={CODES.filter(c => c.status === 'Active').length.toString()} data-id="element-2586" data-mp="8ar61lcmktj3" />
              <KpiTile label="Scheduled" value={CODES.filter(c => c.status === 'Scheduled').length.toString()} data-id="element-2587" data-mp="3ugxuw3nrflq0" />
              <KpiTile label="Total Redemptions" value={CODES.reduce((s, c) => s + c.used, 0).toLocaleString()} data-id="element-2588" data-mp="36vn6loxg0xae" />
              <KpiTile label="XP Distributed" value={CODES.reduce((s, c) => s + c.used * c.xp, 0).toLocaleString()} data-id="element-2589" data-mp="3fu8y49kw7mnb" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2590" data-mp="3ps53ekr5fndi">
              <table className="w-full text-left border-collapse" data-id="element-2591" data-mp="7dvy49l9jeq4">
                <thead data-id="element-2592" data-mp="3dlv95srp4fh">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2593" data-mp="3eeqb9id65hs">
                    <th className="p-4 font-normal" data-id="element-2594" data-mp="hdmru9201pp5">Code</th>
                    <th className="p-4 font-normal" data-id="element-2595" data-mp="cejwxhfqgzuw">Event</th>
                    <th className="p-4 font-normal" data-id="element-2596" data-mp="cbxjj165vq4r">XP</th>
                    <th className="p-4 font-normal" data-id="element-2597" data-mp="3671jwzo1w0lz">Usage</th>
                    <th className="p-4 font-normal" data-id="element-2598" data-mp="3a7bwwwa9frac">Expires</th>
                    <th className="p-4 font-normal" data-id="element-2599" data-mp="3a7v3xhydgv9l">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2600" data-mp="3f5n7xacak7u5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2601" data-mp="3jyzjojvvgk5h">
                  {CODES.map(c => {
                  const pct = Math.round(c.used / c.total * 100);
                  return <tr key={c.code} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2602" data-mp="3tu1adk5spua6">
                        <td className="p-4" data-id="element-2603" data-mp="6r4tmqvrbw23">
                          <span className="font-mono text-[#D4AF37] text-xs tracking-wider" data-id="element-2604" data-mp="6osesmlaq1t9">
                            {c.code}
                          </span>
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2605" data-mp="28ihy5ettc1xm">
                          {c.event}
                        </td>
                        <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2606" data-mp="1w80b2dcy1vpl">
                          +{c.xp}
                        </td>
                        <td className="p-4 w-44" data-id="element-2607" data-mp="1r9p4cbhy41bl">
                          <div className="flex items-center gap-2" data-id="element-2608" data-mp="1m9ue8a5zvfrl">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2609" data-mp="1mbnm1sscg616">
                              <div className="h-full bg-[#D4AF37]" style={{
                            width: `${pct}%`
                          }} data-id="element-2610" data-mp="2jv8ip1xuhgus" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono whitespace-nowrap" data-id="element-2611" data-mp="25zwdu1pz6a05">
                              {c.used}/{c.total}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-[#FDFBF7]/60 text-xs" data-id="element-2612" data-mp="2if2spybhsegv">
                          {c.expires}
                        </td>
                        <td className="p-4" data-id="element-2613" data-mp="2n7dtf3e244ah">
                          <StatusPill status={c.status} data-id="element-2614" data-mp="28hg8qxfc5wq1" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2615" data-mp="2dg9fm704aph8">
                          <div className="flex gap-2 justify-end" data-id="element-2616" data-mp="2dgswdke177to">
                            <button title="Copy code" className="text-[#D4AF37]/70 hover:text-[#D4AF37] p-1.5 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2617" data-mp="315kipv4xwvyj">
                              <Copy className="w-3 h-3" data-id="element-2618" data-mp="2w6r582pnl4yd" />
                            </button>
                            <button title="Edit" className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2619" data-mp="2w8ls3mkhj753">
                              <Edit3 className="w-3 h-3" data-id="element-2620" data-mp="1d6i3jdusurfa" />
                            </button>
                            <button title="Revoke" className="text-red-400/70 hover:text-red-400 p-1.5 border border-red-400/30 hover:bg-red-400/10 transition-colors" data-id="element-2621" data-mp="1886uh5cbwky4">
                              <Trash2 className="w-3 h-3" data-id="element-2622" data-mp="uhbrg9oigy2g" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // COLLECTIBLES
      // ============================================================
      case 'collectibles':
        return <div className="space-y-6" data-id="element-2623" data-mp="6ssli0sxoxdj">
            <SectionHeader title="Collectibles Catalog" eyebrow={`${ARTIFACTS.length} artifacts · ${filteredArtifacts.length} shown`} action={<>
                  <div className="relative" data-id="element-2625" data-mp="10fnawgwwjd67">
                    <Search className="w-4 h-4 text-[#D4AF37]/50 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2626" data-mp="157yd5qf47b1x" />
                    <input type="text" placeholder="Search artifacts..." className="pl-9 pr-4 py-2 bg-[#131B2B] border border-[#D4AF37]/30 text-[#FDFBF7] text-xs focus:outline-none focus:border-[#D4AF37] w-56" data-id="element-2627" data-mp="1a7t2hpj8rskv" />
                  </div>
                  <select value={artifactRarityFilter} onChange={e => setArtifactRarityFilter(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2628" data-mp="mh71ujbkhakw">
                    <option value="All" data-id="element-2629" data-mp="22ybe5ey1729w">All Rarities</option>
                    {rarities.map(r => <option key={r} value={r} data-id="element-2630" data-mp="3ngc8hna7wr7h">
                        {r}
                      </option>)}
                  </select>
                  <button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2631" data-mp="3dnejcyaq03zw">
                    <Plus className="w-3.5 h-3.5" data-id="element-2632" data-mp="34o1iuv4kqprb" /> Add Artifact
                  </button>
                </>} data-id="element-2624" data-mp="6rqx6umwqedv" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2633" data-mp="2zndhbfipyqiu">
              <KpiTile label="Total Artifacts" value={ARTIFACTS.length.toString()} data-id="element-2634" data-mp="2uqn2evini0z0" />
              <KpiTile label="Live" value={ARTIFACTS.filter(a => a.status === 'Live').length.toString()} data-id="element-2635" data-mp="2psbtcn06juhu" />
              <KpiTile label="Draft" value={ARTIFACTS.filter(a => a.status === 'Draft').length.toString()} data-id="element-2636" data-mp="nznlg0clcivq" />
              <KpiTile label="Total Supply" value={ARTIFACTS.reduce((s, a) => s + a.supply, 0).toLocaleString()} data-id="element-2637" data-mp="1sd24rk33xew" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2638" data-mp="3o636neet06z0">
              <table className="w-full text-left border-collapse" data-id="element-2639" data-mp="3aardvck7kqkq">
                <thead data-id="element-2640" data-mp="3f8ki401ybaid">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2641" data-mp="3f92jfb3h0ex5">
                    <th className="p-4 font-normal" data-id="element-2642" data-mp="359lhn4rcal6g">Artifact</th>
                    <th className="p-4 font-normal" data-id="element-2643" data-mp="35a3kik8ibxnc">Rarity</th>
                    <th className="p-4 font-normal" data-id="element-2644" data-mp="zgdejk2qmg0u">Collection</th>
                    <th className="p-4 font-normal" data-id="element-2645" data-mp="zesghkh7sey8">Supply</th>
                    <th className="p-4 font-normal" data-id="element-2646" data-mp="13hovz2bgjuxy">Owned</th>
                    <th className="p-4 font-normal" data-id="element-2647" data-mp="2nzrdjz6a623r">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2648" data-mp="2nxwqae7jdxwi">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2649" data-mp="2xpspjslczu2b">
                  {filteredArtifacts.map(a => {
                  const pct = a.supply > 0 ? Math.round(a.minted / a.supply * 100) : 0;
                  return <tr key={a.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2650" data-mp="37mfaehdoql4c">
                        <td className="p-4" data-id="element-2651" data-mp="3gp6kodutraue">
                          <div className="flex items-center gap-3" data-id="element-2652" data-mp="3gmu6mbs6tkmm">
                            <div className="w-10 h-10 bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center overflow-hidden" data-id="element-2653" data-mp="3br53jygylllk">
                              {a.img ? <img src={a.img} alt={a.name} className="w-full h-full object-cover" data-id="element-2654" data-mp="1hcvszp10vzbv" /> : <ImageIcon className="w-4 h-4 text-[#D4AF37]/50" data-id="element-2655" data-mp="yffakqxhoozz" />}
                            </div>
                            <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2656" data-mp="uelp047irma9">
                              {a.name}
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2657" data-mp="1rhgvqd4qru9">
                          <RarityChip rarity={a.rarity} data-id="element-2658" data-mp="fpfsio02ga62" />
                        </td>
                        <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2659" data-mp="aqckqvspbpue">
                          {a.set}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs font-mono" data-id="element-2660" data-mp="1r31ey1ejjxnw">
                          {a.minted.toLocaleString()} /{' '}
                          {a.supply.toLocaleString()}
                        </td>
                        <td className="p-4 w-32" data-id="element-2661" data-mp="1ukyzhrbzzf5t">
                          <div className="flex items-center gap-2" data-id="element-2662" data-mp="1yo3y3isyiwp6">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2663" data-mp="1ko2l54zf3ytc">
                              <div className="h-full bg-[#D4AF37]" style={{
                            width: `${pct}%`
                          }} data-id="element-2664" data-mp="1pmxc4u7jgtoj" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2665" data-mp="23n864645aodx">
                              {pct}%
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2666" data-mp="2wb43ld1jgkbr">
                          <StatusPill status={a.status} data-id="element-2667" data-mp="2wab2osp9gp1j" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2668" data-mp="313x57s1pncjm">
                          <div className="flex gap-2 justify-end" data-id="element-2669" data-mp="3ayulk4f4ym9w">
                            <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2670" data-mp="3avynmg3afxlg">
                              <Edit3 className="w-3 h-3" data-id="element-2671" data-mp="30ytxy6a8hjzw" />
                            </button>
                            <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2672" data-mp="2wxi8q1v9v0s8">
                              <MoreVertical className="w-4 h-4" data-id="element-2673" data-mp="2s11i910loabz" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // COLLECTIONS
      // ============================================================
      case 'collections':
        return <div className="space-y-6" data-id="element-2674" data-mp="24anwttcit9qd">
            <SectionHeader title="Collection Sets" eyebrow={`${COLLECTIONS.length} sets · ${COLLECTIONS.filter(c => c.status === 'Live').length} live`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2676" data-mp="ss0asacq23bx">
                  <Plus className="w-3.5 h-3.5" data-id="element-2677" data-mp="11lyujo9r12w" /> New Collection
                </button>} data-id="element-2675" data-mp="js3mway5p8hx" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2678" data-mp="525wrpgo28q3">
              {COLLECTIONS.map(c => {
              const pct = Math.round(c.complete / c.cards * 100);
              return <div key={c.name} className="bg-[#131B2B] border border-[#D4AF37]/20 p-5 flex flex-col gap-4" data-id="element-2679" data-mp="etin2cmtgqu0">
                    <div className="flex items-start justify-between" data-id="element-2680" data-mp="3ihc22ulfj1cd">
                      <div className="w-10 h-10 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2681" data-mp="1oz4lobzhb440">
                        <Layers className="w-4 h-4 text-[#D4AF37]" data-id="element-2682" data-mp="1k0jut3649ws8" />
                      </div>
                      <StatusPill status={c.status} data-id="element-2683" data-mp="1xrygxivbme3i" />
                    </div>
                    <div data-id="element-2684" data-mp="1t0q6cji98mor">
                      <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-2685" data-mp="1t0gesrhka7gy">
                        {c.name}
                      </h4>
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase mt-1" data-id="element-2686" data-mp="27nrt3154o5kr">
                        {c.rarity} · Released {c.released}
                      </p>
                    </div>
                    <div data-id="element-2687" data-mp="2gnyc12jlhxrb">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-1.5" data-id="element-2688" data-mp="2coz0gz3cz2qw">
                        <span className="text-[#FDFBF7]/50" data-id="element-2689" data-mp="mdb50vz321kf">Cards Live</span>
                        <span className="text-[#D4AF37] font-mono" data-id="element-2690" data-mp="iay36inginsx">
                          {c.complete} / {c.cards}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2691" data-mp="893uhnsyhk6t">
                        <div className="h-full bg-[#D4AF37]" style={{
                      width: `${pct}%`
                    }} data-id="element-2692" data-mp="3umc2re10jfj0" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-[#D4AF37]/10" data-id="element-2693" data-mp="3kmtlj5chm7un">
                      <button className="flex-1 text-[#D4AF37] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-1.5" data-id="element-2694" data-mp="3pospagc536s4">
                        <Edit3 className="w-3 h-3" data-id="element-2695" data-mp="3gonj29iomefj" /> Edit
                      </button>
                      <button className="flex-1 text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 px-3 py-2 hover:bg-[#FDFBF7]/5 transition-colors flex items-center justify-center gap-1.5" data-id="element-2696" data-mp="19wv1kv49b32k">
                        <Eye className="w-3 h-3" data-id="element-2697" data-mp="1ce6oll92fqug" /> View
                      </button>
                    </div>
                  </div>;
            })}
            </div>
          </div>;
      // ============================================================
      // LIVE EVENTS
      // ============================================================
      case 'events':
        return <div className="space-y-6" data-id="element-2698" data-mp="1r5fg5zwj3ma8">
            <SectionHeader title="Live Events Calendar" eyebrow={`${EVENTS_NIGHTS.length + EVENTS_SESSIONS.length} events scheduled`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2700" data-mp="1hge24f9jneyw">
                  <Plus className="w-3.5 h-3.5" data-id="element-2701" data-mp="2a3rt7wqk9gb3" /> Schedule Event
                </button>} data-id="element-2699" data-mp="1r6gv4fgo0sx8" />

            <div className="flex gap-2" data-id="element-2702" data-mp="2hg7qlufi0oyo">
              <button onClick={() => setEventSubTab('nights')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${eventSubTab === 'nights' ? 'border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2703" data-mp="2hhiya5c2egv2">
                <MapPin className="w-3.5 h-3.5" data-id="element-2704" data-mp="30dis7lolruha" /> Treasury Nights (
                {EVENTS_NIGHTS.length})
              </button>
              <button onClick={() => setEventSubTab('sessions')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${eventSubTab === 'sessions' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2705" data-mp="35bak4fp8zglh">
                <Video className="w-3.5 h-3.5" data-id="element-2706" data-mp="3o4o9m5a6yutp" /> Court Sessions (
                {EVENTS_SESSIONS.length})
              </button>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2707" data-mp="97u5u7fl1qm0">
              <table className="w-full text-left border-collapse" data-id="element-2708" data-mp="9a6luf1s5qwf">
                <thead data-id="element-2709" data-mp="n73aqayc8yao">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2710" data-mp="n5ipxd1tstq0">
                    <th className="p-4 font-normal" data-id="element-2711" data-mp="3qobtcx0w426g">Date</th>
                    <th className="p-4 font-normal" data-id="element-2712" data-mp="3qolikie425b3">Event</th>
                    <th className="p-4 font-normal" data-id="element-2713" data-mp="3lq0iciyuzlmn">Location</th>
                    <th className="p-4 font-normal" data-id="element-2714" data-mp="48r0smzlrke1">Rank Gate</th>
                    <th className="p-4 font-normal" data-id="element-2715" data-mp="4abukae3ezau">Code</th>
                    <th className="p-4 font-normal" data-id="element-2716" data-mp="3vmwvj1zpcvxj">Attendance</th>
                    <th className="p-4 font-normal" data-id="element-2717" data-mp="3vllngpz8520m">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2718" data-mp="37w9hs033vc32">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2719" data-mp="32wn5ptz0k1vu">
                  {(eventSubTab === 'nights' ? EVENTS_NIGHTS : EVENTS_SESSIONS).map(e => {
                  const pct = e.capacity > 0 ? Math.round(e.attended / e.capacity * 100) : 0;
                  return <tr key={e.code} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2720" data-mp="2176s64p94rlp">
                        <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2721" data-mp="265rhva6vnsvw">
                          {e.date}
                        </td>
                        <td className="p-4 text-[#FDFBF7] text-sm" data-id="element-2722" data-mp="1x5la84xv1n4n">
                          {e.title}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2723" data-mp="gqbjmpuhrrks">
                          {e.location}
                        </td>
                        <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2724" data-mp="lod1x8k3fmv7">
                          {e.rank}
                        </td>
                        <td className="p-4" data-id="element-2725" data-mp="3kxeztcckgt5q">
                          <span className="font-mono text-[#D4AF37] text-xs" data-id="element-2726" data-mp="3kw3uh83h3dci">
                            {e.code}
                          </span>
                        </td>
                        <td className="p-4 w-36" data-id="element-2727" data-mp="3txbqfjiio23f">
                          <div className="flex items-center gap-2" data-id="element-2728" data-mp="3tw0m9il5ssbs">
                            <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2729" data-mp="2x9nmq6j7cc3e">
                              <div className={`h-full ${eventSubTab === 'nights' ? 'bg-[#8B0000]' : 'bg-[#D4AF37]'}`} style={{
                            width: `${pct}%`
                          }} data-id="element-2730" data-mp="3252wama6flu0" />
                            </div>
                            <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2731" data-mp="2iiodquegflja">
                              {e.attended}/{e.capacity}
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-2732" data-mp="2613x8y71fqfp">
                          <StatusPill status={e.status} data-id="element-2733" data-mp="21yqr48odpqie" />
                        </td>
                        <td className="p-4 text-right" data-id="element-2734" data-mp="220tsfouzf62f">
                          <div className="flex gap-2 justify-end" data-id="element-2735" data-mp="1wz4f22kaegb6">
                            <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2736" data-mp="1s1copczaj293">
                              <Edit3 className="w-3 h-3" data-id="element-2737" data-mp="1n80032um2vgc" />
                            </button>
                            <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2738" data-mp="1i8x5ydg669n5">
                              <MoreVertical className="w-4 h-4" data-id="element-2739" data-mp="1e7ldm05wvab9" />
                            </button>
                          </div>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // TRADES
      // ============================================================
      case 'trades':
        return <div className="space-y-6" data-id="element-2740" data-mp="pojn587i0xdt">
            <SectionHeader title="Trades & Open Market" eyebrow="Trade volume · Darbar fees · moderation" data-id="element-2741" data-mp="ppkyzewc9nwl" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2742" data-mp="1dggmhztrhwy1">
              <KpiTile label="Volume 7d" value="1,240" trend="-5%" up={false} data-id="element-2743" data-mp="1icnurz9mrkj5" />
              <KpiTile label="Open Listings" value="412" data-id="element-2744" data-mp="13n9kmoy8cz2p" />
              <KpiTile label="Darbar Fees (7d)" value="1,240 XP" trend="+8%" up data-id="element-2745" data-mp="18ne2aiavjzui" />
              <KpiTile label="Flagged" value={TRADES_FLAGGED.length.toString()} trend="Review" up={false} data-id="element-2746" data-mp="1wbf6jf10su44" />
            </div>

            <div className="flex gap-2" data-id="element-2747" data-mp="3lsiywnurrp06">
              <button onClick={() => setTradeSubTab('recent')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border transition-colors ${tradeSubTab === 'recent' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2748" data-mp="3gvahiphg3ozz">
                Recent ({TRADES_RECENT.length})
              </button>
              <button onClick={() => setTradeSubTab('flagged')} className={`px-4 py-2.5 text-[10px] uppercase tracking-widest border flex items-center gap-2 transition-colors ${tradeSubTab === 'flagged' ? 'border-red-400 text-red-400 bg-red-400/10' : 'border-[#D4AF37]/20 text-[#FDFBF7]/50 hover:text-[#FDFBF7]'}`} data-id="element-2749" data-mp="2xwoeh52dbntf">
                <Flag className="w-3.5 h-3.5" data-id="element-2750" data-mp="r7jljx2vmdyg" /> Flagged (
                {TRADES_FLAGGED.length})
              </button>
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2751" data-mp="1if1xk279nh7l">
              <table className="w-full text-left border-collapse" data-id="element-2752" data-mp="1mfc1l9bhzxjt">
                <thead data-id="element-2753" data-mp="1mhyb3ehy8n4o">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2754" data-mp="1rf84f58glixs">
                    <th className="p-4 font-normal" data-id="element-2755" data-mp="1webb0u437x7v">Trade ID</th>
                    <th className="p-4 font-normal" data-id="element-2756" data-mp="21d4gq0d4ujxh">From → To</th>
                    <th className="p-4 font-normal" data-id="element-2757" data-mp="21cw1w2xg94jn">Artifact</th>
                    <th className="p-4 font-normal" data-id="element-2758" data-mp="25dy2oo70lonq">Value</th>
                    <th className="p-4 font-normal" data-id="element-2759" data-mp="25cdc19ntmpv5">Fee</th>
                    {tradeSubTab === 'flagged' && <th className="p-4 font-normal" data-id="element-2760" data-mp="2f49ib8029mag">Reason</th>}
                    <th className="p-4 font-normal" data-id="element-2761" data-mp="2k3m5xuyraxe3">Time</th>
                    <th className="p-4 font-normal" data-id="element-2762" data-mp="2k5703jh5sebf">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2763" data-mp="2p277bxpqvn44">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2764" data-mp="dgeusse7ac6l">
                  {(tradeSubTab === 'recent' ? TRADES_RECENT : TRADES_FLAGGED).map((t: any) => <tr key={t.id} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2765" data-mp="3hftxvzofku5">
                      <td className="p-4 font-mono text-[#D4AF37] text-xs" data-id="element-2766" data-mp="w6ebaqvz6iye">
                        {t.id}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/80 text-xs" data-id="element-2767" data-mp="r7a5894cl1ca">
                        <span className="font-mono" data-id="element-2768" data-mp="mf9z1yxopc79">{t.from}</span>
                        <ArrowRight className="w-3 h-3 inline mx-1.5 text-[#D4AF37]/40" data-id="element-2769" data-mp="hgyoflzkexnz" />
                        <span className="font-mono" data-id="element-2770" data-mp="hf4d8ze3ijx3">{t.to}</span>
                      </td>
                      <td className="p-4 text-[#FDFBF7] text-xs" data-id="element-2771" data-mp="1eyfl2oaaftml">
                        {t.artifact}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2772" data-mp="34dq9ipkt6iec">
                        {t.value}
                      </td>
                      <td className="p-4 text-[#FDFBF7]/60 text-xs font-mono" data-id="element-2773" data-mp="39b9mlm5rsp4p">
                        {t.fee}
                      </td>
                      {tradeSubTab === 'flagged' && <td className="p-4 text-red-400/80 text-xs" data-id="element-2774" data-mp="2zlq4wjtsn1bf">
                          {t.reason}
                        </td>}
                      <td className="p-4 text-[#FDFBF7]/50 text-xs" data-id="element-2775" data-mp="2polpr3yd8644">
                        {t.time}
                      </td>
                      <td className="p-4" data-id="element-2776" data-mp="2bpm2o0qi3za2">
                        <StatusPill status={t.status} data-id="element-2777" data-mp="2bqx4vw4asyz2" />
                      </td>
                      <td className="p-4 text-right" data-id="element-2778" data-mp="2gofdtmibfe72">
                        {tradeSubTab === 'flagged' ? <div className="flex gap-2 justify-end" data-id="element-2779" data-mp="9y90hjfx45hp">
                            <button className="text-emerald-400/80 hover:text-emerald-400 text-[9px] uppercase tracking-widest border border-emerald-400/30 px-2 py-1 hover:bg-emerald-400/10 transition-colors" data-id="element-2780" data-mp="evhhvht8s5hw">
                              Approve
                            </button>
                            <button className="text-red-400/80 hover:text-red-400 text-[9px] uppercase tracking-widest border border-red-400/30 px-2 py-1 hover:bg-red-400/10 transition-colors" data-id="element-2781" data-mp="294mmick5sr3z">
                              Reverse
                            </button>
                          </div> : <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2782" data-mp="2d5g992lv7zva">
                            <MoreVertical className="w-4 h-4" data-id="element-2783" data-mp="2s2h2xbnma4we" />
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // DAILY PACKET
      // ============================================================
      case 'packets':
        return <div className="space-y-6" data-id="element-2784" data-mp="buqz0rshgnf5">
            <SectionHeader title="Daily Packet Engine" eyebrow="Hero card · drop weights · open-rate" action={<button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2786" data-mp="lkjzoxr37i4m">
                  <Plus className="w-3.5 h-3.5" data-id="element-2787" data-mp="3p3myylxvnryo" /> Issue Bonus Packet
                </button>} data-id="element-2785" data-mp="btpdtuhr6kjp" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2788" data-mp="3u03qzr876qh1">
              {/* Today's hero */}
              <div className="bg-[#131B2B] border border-[#D4AF37]/30 p-5 relative" data-id="element-2789" data-mp="1z75gc1e60tk">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2790" data-mp="19ao4v9jxbpxq" />
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-2791" data-mp="1devud8y7h9x0">
                  Today's Hero Card · Day 47
                </p>
                <div className="aspect-[4/5] bg-[#0B0F19] border border-[#D4AF37]/30 mb-4 overflow-hidden" data-id="element-2792" data-mp="2turscask7o49">
                  <img src={RANJIT_IMG} alt="Maharaja Ranjit Singh" className="w-full h-full object-cover" data-id="element-2793" data-mp="2p174vcolecjb" />
                </div>
                <h4 className="font-serif text-xl text-[#FDFBF7] leading-tight mb-1" data-id="element-2794" data-mp="2f5vpqt99v3b1">
                  Maharaja Ranjit Singh
                </h4>
                <RarityChip rarity="Founders' Edition" data-id="element-2795" data-mp="263dz82ml3oi8" />
                <div className="mt-4 grid grid-cols-2 gap-2 text-center" data-id="element-2796" data-mp="26606s29h640g">
                  <div className="border border-[#D4AF37]/15 p-2" data-id="element-2797" data-mp="2164c4tmo3oxk">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-2798" data-mp="4d2kkpa7e9oh">2,140</p>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2799" data-mp="3viu7lfsop672">
                      Opened
                    </p>
                  </div>
                  <div className="border border-[#D4AF37]/15 p-2" data-id="element-2800" data-mp="20ql8nxey21wh">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-2801" data-mp="1vxii7y5gr8ki">82%</p>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-2802" data-mp="1qyyze6m62cwd">
                      Open Rate
                    </p>
                  </div>
                </div>
                <button className="mt-3 w-full px-3 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-2803" data-mp="2joo0sl2sgeyi">
                  <Edit3 className="w-3 h-3" data-id="element-2804" data-mp="2amer1xfcgpm6" /> Replace Hero
                </button>
              </div>

              {/* Drop weights */}
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2805" data-mp="25qhb3s7ztl9x">
                <div className="flex items-end justify-between mb-4" data-id="element-2806" data-mp="z9m3kytl6nbx">
                  <div data-id="element-2807" data-mp="u86dutggrsn7">
                    <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2808" data-mp="u7wn33nlhhgg">
                      Rarity Drop Weights
                    </h3>
                    <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2809" data-mp="3av4qihnzzt4a">
                      Must total 100%
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-mono ${dropTotal === 100 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-2810" data-mp="3jnpu2qj25t8j">
                    Total: {dropTotal}%
                  </span>
                </div>
                <div className="space-y-4" data-id="element-2811" data-mp="3to8mi9ooui22">
                  {[{
                  key: 'founders' as const,
                  label: "Founders' Edition",
                  value: dropWeights.founders,
                  color: '#D4AF37'
                }, {
                  key: 'rare' as const,
                  label: 'Treasury Rare',
                  value: dropWeights.rare,
                  color: '#D4AF37'
                }, {
                  key: 'epic' as const,
                  label: 'Epic',
                  value: dropWeights.epic,
                  color: '#8B0000'
                }, {
                  key: 'standard' as const,
                  label: 'Standard',
                  value: dropWeights.standard,
                  color: '#FDFBF7'
                }].map(row => <div key={row.key} data-id="element-2812" data-mp="3phdnapq0d76f">
                      <div className="flex items-center justify-between text-xs mb-2" data-id="element-2813" data-mp="83hyda9m60s2">
                        <span className="text-[#FDFBF7]/80" data-id="element-2814" data-mp="345d2tye51rl">{row.label}</span>
                        <div className="flex items-center gap-2" data-id="element-2815" data-mp="369dh1x2zin4">
                          <input type="number" min={0} max={100} value={row.value} onChange={e => setDropWeights({
                        ...dropWeights,
                        [row.key]: Math.max(0, Math.min(100, Number(e.target.value) || 0))
                      })} className="w-16 bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-xs font-mono px-2 py-1 text-right focus:outline-none focus:border-[#D4AF37]" data-id="element-2816" data-mp="h4g1lqc8a49l" />
                          <span className="text-[#D4AF37]/60 text-xs font-mono" data-id="element-2817" data-mp="33izgpgtbmvr">
                            %
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-[#0B0F19] border border-[#D4AF37]/15 overflow-hidden" data-id="element-2818" data-mp="3q779gmwz76qy">
                        <div className="h-full transition-all" style={{
                      width: `${row.value}%`,
                      background: row.color,
                      opacity: 0.7
                    }} data-id="element-2819" data-mp="3v4p8nll6y7lr" />
                      </div>
                    </div>)}
                </div>
                <div className="flex gap-3 mt-6 pt-4 border-t border-[#D4AF37]/10" data-id="element-2820" data-mp="39bl8ozqlxzx6">
                  <button onClick={() => setDropWeights({
                  founders: 1,
                  rare: 8,
                  epic: 22,
                  standard: 69
                })} className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 px-3 py-2 hover:bg-[#FDFBF7]/5 transition-colors flex items-center gap-2" data-id="element-2821" data-mp="34fmfmyuc3tqf">
                    <RotateCcw className="w-3 h-3" data-id="element-2822" data-mp="olce9cnq81tm" /> Reset to default
                  </button>
                  <button disabled={dropTotal !== 100} className="ml-auto px-4 py-2 bg-[#D4AF37] disabled:bg-[#D4AF37]/30 disabled:cursor-not-allowed text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2823" data-mp="26iz7bhe92psh">
                    <Save className="w-3 h-3" data-id="element-2824" data-mp="1nmplu910qwyg" /> Save Weights
                  </button>
                </div>
              </div>
            </div>

            {/* Open-rate chart */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2825" data-mp="1wmvw7m1f7gte">
              <div className="flex items-end justify-between mb-4" data-id="element-2826" data-mp="1woqfkvx1n0vd">
                <div data-id="element-2827" data-mp="21mick4cjpd6f">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2828" data-mp="21jm9k1lubyb4">
                    Open Rate Trend
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mt-1.5" data-id="element-2829" data-mp="3d0ckfy17leb2">
                    14-day daily packet open rate (%)
                  </p>
                </div>
                <span className="text-emerald-400 text-[10px] tracking-widest uppercase flex items-center gap-1" data-id="element-2830" data-mp="18xkh2bwqfxnt">
                  <TrendingUp className="w-3 h-3" data-id="element-2831" data-mp="1mx2bp7wpm553" /> +6% wk
                </span>
              </div>
              <ActivityChart data={OPENRATE_14D} height={140} data-id="element-2832" data-mp="1hxfu6m9um2qh" />
            </div>

            {/* Upcoming schedule */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2833" data-mp="u8wma7upnryx">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2834" data-mp="p9tjku27dxu4">
                Upcoming Hero Cards · Next 7 Days
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-2" data-id="element-2835" data-mp="13z98s5m35v7p">
                {[{
                d: 'Tomorrow',
                card: 'Hari Singh Nalwa',
                r: 'Epic',
                img: NALWA_IMG
              }, {
                d: '+2d',
                card: 'Akali Phula Singh',
                r: 'Epic',
                img: NALWA_IMG
              }, {
                d: '+3d',
                card: 'Moran Sarkar',
                r: 'Epic',
                img: MORAN_IMG
              }, {
                d: '+4d',
                card: 'Koh-i-Noor',
                r: 'Treasury Rare',
                img: RANJIT_IMG
              }, {
                d: '+5d',
                card: 'Jind Kaur',
                r: 'Epic',
                img: JIND_IMG
              }, {
                d: '+6d',
                card: 'Sukerchakia Seal',
                r: 'Treasury Rare',
                img: RANJIT_IMG
              }, {
                d: '+7d',
                card: 'Maharaja Ranjit Singh',
                r: "Founders' Edition",
                img: RANJIT_IMG
              }].map(u => <div key={u.d} className="p-3 border border-[#D4AF37]/15 bg-[#0B0F19] text-center" data-id="element-2836" data-mp="283llsvscyvtr">
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-widest uppercase mb-2" data-id="element-2837" data-mp="236v8uhbuogck">
                      {u.d}
                    </p>
                    <div className="w-full aspect-[4/5] bg-[#131B2B] border border-[#D4AF37]/20 mb-2 overflow-hidden flex items-center justify-center" data-id="element-2838" data-mp="2vso2o1f64kn7">
                      {u.img ? <img src={u.img} alt={u.card} className="w-full h-full object-cover" data-id="element-2839" data-mp="2m34k6wvdassv" /> : <ImageIcon className="w-4 h-4 text-[#D4AF37]/30" data-id="element-2840" data-mp="3ekz1nk6pbyg1" />}
                    </div>
                    <p className="text-[#FDFBF7] text-[11px] leading-tight mb-1" data-id="element-2841" data-mp="39re0xkdq4qd5">
                      {u.card}
                    </p>
                    <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-2842" data-mp="3m3gikbgwqfj1">
                      {u.r}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // QUESTS
      // ============================================================
      case 'quests':
        return <div className="space-y-6" data-id="element-2843" data-mp="8vvezw2mpggl">
            <SectionHeader title="XP Quests" eyebrow={`${QUESTS.length} quests · ${QUESTS.filter(q => q.status === 'Active').length} active`} action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2845" data-mp="iou9fukt4184">
                  <Plus className="w-3.5 h-3.5" data-id="element-2846" data-mp="rllciqxs0gc7" /> Create Quest
                </button>} data-id="element-2844" data-mp="8utwx7n73tpd" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2847" data-mp="1zbwpqyn4wogh">
              <KpiTile label="Active Quests" value={QUESTS.filter(q => q.status === 'Active').length.toString()} data-id="element-2848" data-mp="1zd7t4xco3u72" />
              <KpiTile label="Avg Completion" value="32%" trend="+4%" up data-id="element-2849" data-mp="1pdz32fkxazpz" />
              <KpiTile label="XP Awarded 7d" value="42,150" trend="+12%" up data-id="element-2850" data-mp="2d4cm5glj5o8f" />
              <KpiTile label="One-shot Quests" value={QUESTS.filter(q => q.cadence === 'One-shot').length.toString()} data-id="element-2851" data-mp="2i2xlldsyk3vt" />
            </div>

            <div className="bg-[#131B2B] border border-[#D4AF37]/20 overflow-x-auto" data-id="element-2852" data-mp="28ba15wutjn3u">
              <table className="w-full text-left border-collapse" data-id="element-2853" data-mp="2w1xe4wjld2x9">
                <thead data-id="element-2854" data-mp="30svtntwr9wzg">
                  <tr className="border-b border-[#D4AF37]/20 text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2855" data-mp="30t5kfjpmk867">
                    <th className="p-4 font-normal" data-id="element-2856" data-mp="pclueyq6yuj5">Quest</th>
                    <th className="p-4 font-normal" data-id="element-2857" data-mp="kf3n1crtond9">Trigger</th>
                    <th className="p-4 font-normal" data-id="element-2858" data-mp="kcprz3x57b5p">Reward</th>
                    <th className="p-4 font-normal" data-id="element-2859" data-mp="z2om8vlku2fw">Cadence</th>
                    <th className="p-4 font-normal" data-id="element-2860" data-mp="u4vi083u3ii9">Completion</th>
                    <th className="p-4 font-normal" data-id="element-2861" data-mp="u6q3pomxjene">Status</th>
                    <th className="p-4 font-normal text-right" data-id="element-2862" data-mp="6g3tprtd7kar">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10" data-id="element-2863" data-mp="1hivtz1l5cph">
                  {QUESTS.map(q => <tr key={q.name} className="hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2864" data-mp="fh218k30i9y3">
                      <td className="p-4" data-id="element-2865" data-mp="1qucsd0drhsqa">
                        <div className="flex items-center gap-3" data-id="element-2866" data-mp="1qt2vgeinaql8">
                          <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-2867" data-mp="1ms96rixdp7ra">
                            <Target className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2868" data-mp="jhvg588lo8d9" />
                          </div>
                          <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2869" data-mp="ofn6hxtljmfc">
                            {q.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-[#FDFBF7]/70 text-xs" data-id="element-2870" data-mp="xg21jowd4fw1">
                        {q.trigger}
                      </td>
                      <td className="p-4 text-[#D4AF37] text-xs font-mono" data-id="element-2871" data-mp="5qqiucspfs63">
                        {q.reward}
                      </td>
                      <td className="p-4" data-id="element-2872" data-mp="9rsc8czxxa05">
                        <span className="text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/15 px-2 py-1" data-id="element-2873" data-mp="ejaauuehqyhy">
                          {q.cadence}
                        </span>
                      </td>
                      <td className="p-4 w-36" data-id="element-2874" data-mp="3i7lrypmg6hg5">
                        <div className="flex items-center gap-2" data-id="element-2875" data-mp="3i6ansop3b7oi">
                          <div className="flex-1 h-1.5 bg-[#0B0F19] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2876" data-mp="3ankx4ee261ej">
                            <div className="h-full bg-[#D4AF37]" style={{
                          width: `${q.completion}%`
                        }} data-id="element-2877" data-mp="35qaxy70vabdi" />
                          </div>
                          <span className="text-[#FDFBF7]/60 text-[10px] font-mono" data-id="element-2878" data-mp="2rqs1wclbyqhk">
                            {q.completion}%
                          </span>
                        </div>
                      </td>
                      <td className="p-4" data-id="element-2879" data-mp="2i0gntx7xn0wy">
                        <StatusPill status={q.status} data-id="element-2880" data-mp="2dyvf8o2fl8sk" />
                      </td>
                      <td className="p-4 text-right" data-id="element-2881" data-mp="28zit65jdw5r1">
                        <div className="flex gap-2 justify-end" data-id="element-2882" data-mp="28y7fne04zjmq">
                          <button className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] p-1.5 border border-[#FDFBF7]/20 hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-2883" data-mp="23zw4mzy3v32x">
                            <Edit3 className="w-3 h-3" data-id="element-2884" data-mp="7zdw2g3c6oqf" />
                          </button>
                          <button className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors p-1" data-id="element-2885" data-mp="cvkr3du72ftl">
                            <MoreVertical className="w-4 h-4" data-id="element-2886" data-mp="3uarjes04gryj" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      // ============================================================
      // REFERRALS
      // ============================================================
      case 'referrals':
        return <div className="space-y-6" data-id="element-2887" data-mp="3gdmewwg2415e">
            <SectionHeader title="Referrals & Royal Decrees" eyebrow="Top referrers · funnel · tier rewards" data-id="element-2888" data-mp="19mmz44lqgp3p" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2889" data-mp="1el7rjhuqu4hl">
              <KpiTile label="Total Invites Sent" value="3,420" trend="+142" up data-id="element-2890" data-mp="2z2z3ano7soji" />
              <KpiTile label="Signups from Invites" value="1,820" trend="+82" up data-id="element-2891" data-mp="2u2uiwmk6r9o0" />
              <KpiTile label="Conversion Rate" value="53%" trend="+3%" up data-id="element-2892" data-mp="3423bpc3bei8s" />
              <KpiTile label="Top Referrer XP" value="8,400" data-id="element-2893" data-mp="3htgtakhnx616" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-2894" data-mp="3cvf8nv4l8ynl">
              {/* Leaderboard */}
              <div className="lg:col-span-3 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2895" data-mp="3rs62zpzenk6c">
                <div className="flex items-center justify-between mb-4" data-id="element-2896" data-mp="3mtmcdc9v9k7n">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2897" data-mp="3mr9vz3jrbhwp">
                    Top Referrers
                  </h3>
                  <span className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest" data-id="element-2898" data-mp="1sfv52jf0fcfd">
                    All time
                  </span>
                </div>
                <div className="divide-y divide-[#D4AF37]/10" data-id="element-2899" data-mp="2i5tvx9ipi17h">
                  {REFERRERS.map(r => <div key={r.handle} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0" data-id="element-2900" data-mp="2r45h2w0t06sj">
                      <span className={`w-7 text-center font-mono text-sm ${r.rank <= 3 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-2901" data-mp="2w4jr2jlytqt7">
                        #{r.rank}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-[#0B0F19] border border-[#D4AF37]/30 flex items-center justify-center shrink-0" data-id="element-2902" data-mp="39ux0yrif3fl6">
                        <span className="text-[#D4AF37] text-xs font-serif" data-id="element-2903" data-mp="3et89mywz7k1t">
                          {r.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2904" data-mp="3osz4x2td232y">
                        <p className="text-[#FDFBF7] text-sm leading-tight truncate" data-id="element-2905" data-mp="1d4kihr7ge0k1">
                          {r.name}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5 truncate" data-id="element-2906" data-mp="184odppqxm53f">
                          {r.handle}
                        </p>
                      </div>
                      <div className="text-right grid grid-cols-3 gap-4 text-xs" data-id="element-2907" data-mp="y834es7vq7ts">
                        <div data-id="element-2908" data-mp="1vvlfi9p0jm0y">
                          <p className="text-[#FDFBF7]/80 font-mono" data-id="element-2909" data-mp="1vtqukvdqrtwv">
                            {r.invites}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2910" data-mp="1qybjgb74cc45">
                            Invites
                          </p>
                        </div>
                        <div data-id="element-2911" data-mp="1h430ae3z9vin">
                          <p className="text-[#FDFBF7]/80 font-mono" data-id="element-2912" data-mp="2em34q4amzzhk">
                            {r.signups}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2913" data-mp="29ta15jqwmx72">
                            Signups
                          </p>
                        </div>
                        <div data-id="element-2914" data-mp="1myrz8j43zfx7">
                          <p className="text-[#D4AF37] font-mono" data-id="element-2915" data-mp="1n0346m9aitpw">{r.xp}</p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2916" data-mp="19852uxltm24a">
                            XP
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Tier rewards */}
              <div className="lg:col-span-2 bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2917" data-mp="1rx1wcz9e8qlg">
                <div className="flex items-center justify-between mb-4" data-id="element-2918" data-mp="1rzfop0covopb">
                  <h3 className="text-[#FDFBF7] font-serif text-lg leading-none" data-id="element-2919" data-mp="1wul9twy9ozcc">
                    Reward Tiers
                  </h3>
                  <button className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest" data-id="element-2920" data-mp="lgzn0ifvr9tz">
                    Edit
                  </button>
                </div>
                <div className="space-y-3" data-id="element-2921" data-mp="dzb86etouu2s">
                  {TIER_REWARDS.map(t => <div key={t.count} className="flex items-center gap-3 p-3 border border-[#D4AF37]/15 bg-[#0B0F19]" data-id="element-2922" data-mp="90hvupq51947">
                      <div className="w-10 h-10 border border-[#D4AF37]/40 bg-[#131B2B] flex items-center justify-center shrink-0" data-id="element-2923" data-mp="hzv1f7b55djn">
                        <span className="text-[#D4AF37] font-serif text-base" data-id="element-2924" data-mp="3mdjtd1tvsikx">
                          {t.count}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2925" data-mp="41dlvwve37ct">
                        <p className="text-[#D4AF37] text-xs font-bold leading-tight" data-id="element-2926" data-mp="3vcx5615h9j8t">
                          {t.reward}
                        </p>
                        <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5 leading-tight" data-id="element-2927" data-mp="37m16ck2ltns2">
                          {t.label}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Funnel */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2928" data-mp="2fabo2h91kp6p">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2929" data-mp="2fcedaz2deead">
                Referral Funnel · 30d
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-id="element-2930" data-mp="26i9qcqqcxn04">
                {[{
                label: 'Invites Sent',
                value: '3,420',
                pct: 100
              }, {
                label: 'Link Clicks',
                value: '2,180',
                pct: 64
              }, {
                label: 'Signups',
                value: '1,820',
                pct: 53
              }, {
                label: 'Activated',
                value: '1,490',
                pct: 44
              }].map((step, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/15 p-4 flex flex-col gap-2" data-id="element-2931" data-mp="13xvo7zjthuml" data-mpi={i}>
                    <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest" data-id="element-2932" data-mp="1huqyfvbt1s48">
                      {step.label}
                    </p>
                    <p className="font-serif text-2xl text-[#D4AF37]" data-id="element-2933" data-mp="1rtzy94tcq0yi">
                      {step.value}
                    </p>
                    <div className="h-1 bg-[#131B2B] border border-[#D4AF37]/20 overflow-hidden" data-id="element-2934" data-mp="1mu3tv4gqs7if">
                      <div className="h-full bg-[#D4AF37]" style={{
                    width: `${step.pct}%`
                  }} data-id="element-2935" data-mp="20ppe6yc1634i" />
                    </div>
                    <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-2936" data-mp="25n7puwhfmwg7">
                      {step.pct}% of sent
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // AUDIT LOG
      // ============================================================
      case 'audit':
        return <div className="space-y-6" data-id="element-2937" data-mp="1h1zkt6ykjorl">
            <SectionHeader title="Audit Log" eyebrow={`${AUDIT_LOG.length} actions · last 7 days`} action={<>
                  <select className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2939" data-mp="1r0p2ozqgt97u">
                    <option data-id="element-2940" data-mp="1zy93y8obv5ig">All admins</option>
                    <option data-id="element-2941" data-mp="1zx6b3v7g2szw">@harnoor_s</option>
                    <option data-id="element-2942" data-mp="nhql4wmfuz2">@ranjit_s</option>
                  </select>
                  <select className="bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-xs px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2943" data-mp="5l9lm7skbx7h">
                    <option data-id="element-2944" data-mp="3u8pep9w5xayt">All actions</option>
                    <option data-id="element-2945" data-mp="3pg5y0426hzps">XP Grant</option>
                    <option data-id="element-2946" data-mp="3pfd1dvwxoqld">Event</option>
                    <option data-id="element-2947" data-mp="3khbipc3f6taf">Artifact</option>
                    <option data-id="element-2948" data-mp="3kics7c4sf7q1">Misl</option>
                    <option data-id="element-2949" data-mp="3fii4fhgb6y97">Trade</option>
                    <option data-id="element-2950" data-mp="3fjjebill9epc">Settings</option>
                  </select>
                  <button className="px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2" data-id="element-2951" data-mp="h0m9ficgd5h2">
                    <Download className="w-3.5 h-3.5" data-id="element-2952" data-mp="72evz9hdh5jt" /> Export
                  </button>
                </>} data-id="element-2938" data-mp="1m018k56xwc9e" />

            <div className="bg-[#131B2B] border border-[#D4AF37]/20" data-id="element-2953" data-mp="301tctxx9nr9">
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-2954" data-mp="2rsnpmucfizmb">
                {AUDIT_LOG.map((row, i) => <div key={i} className="flex flex-wrap items-center gap-3 px-5 py-4 hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2955" data-mp="2nqspvua5cuan" data-mpi={i}>
                    <span className="font-mono text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest w-44 shrink-0" data-id="element-2956" data-mp="k92qrqnb615h">
                      {row.ts}
                    </span>
                    <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-[#D4AF37]/30 text-[#D4AF37]/80 shrink-0" data-id="element-2957" data-mp="5iloy66rh982">
                      {row.kind}
                    </span>
                    <p className="text-[#FDFBF7]/80 text-sm flex-1 min-w-0" data-id="element-2958" data-mp="y7apa2zztair">
                      <span className="text-[#D4AF37] font-mono" data-id="element-2959" data-mp="137f8w1w76ld7">
                        {row.admin}
                      </span>{' '}
                      <span className="text-[#FDFBF7]/60" data-id="element-2960" data-mp="t9r4ek8shzhu">{row.action}</span>{' '}
                      <span className="text-[#FDFBF7]" data-id="element-2961" data-mp="tbvi1twhqcvb">{row.target}</span>
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      // ============================================================
      // RESTORATION MANAGER
      // ============================================================
      case 'restoration':
        return <div className="space-y-6" data-id="element-2962" data-mp="3gewtb18j7f3u">
            <SectionHeader title="Restoration Manager" eyebrow="Zones · Schools · Milestones" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2964" data-mp="3bdz6ucghnywj">
                  <Plus className="w-3.5 h-3.5" data-id="element-2965" data-mp="2ip1t8mn82qa4" /> New Zone
                </button>} data-id="element-2963" data-mp="3gdvb8ct3lscm" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2966" data-mp="2xhvlz9ri92wq">
              <div className="lg:col-span-2 space-y-6" data-id="element-2967" data-mp="2snrdun6i6ou2">
                <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-6" data-id="element-2968" data-mp="24z81nwkc1rwp">
                  <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2969" data-mp="200oe5rq3c828">
                    Active Zones
                  </h3>
                  <div className="space-y-3" data-id="element-2970" data-mp="2pplq229nhq7c">
                    {['Lahore Court', 'Sacred Punjab', 'Frontier Defenders', 'Treasury Vault'].map((zone, i) => <div key={i} className="flex items-center justify-between p-3 bg-[#0B0F19] border border-white/5" data-id="element-2971" data-mp="33fxlob5g2738" data-mpi={i}>
                        <div data-id="element-2972" data-mp="2arjo1wi9pcs7">
                          <p className="text-[#FDFBF7] text-sm" data-id="element-2973" data-mp="2aqrs2kkrdd1e">{zone}</p>
                          <p className="text-[#FDFBF7]/40 text-xs" data-id="element-2974" data-mp="2fqw7s8due3qk">
                            Progress: {Math.floor(Math.random() * 80) + 10}%
                          </p>
                        </div>
                        <button className="text-[#D4AF37] text-xs hover:underline" data-id="element-2975" data-mp="3r61mkar8zsqj">
                          Edit
                        </button>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2976" data-mp="1kgre0337mc4a">
                <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-6" data-id="element-2977" data-mp="1fhwiq1o22v3a">
                  <h3 className="text-[#FDFBF7] font-serif text-lg mb-4" data-id="element-2978" data-mp="1arpx8aoubqp9">
                    School Progress
                  </h3>
                  <div className="space-y-3" data-id="element-2979" data-mp="15teltviwd84x">
                    {['Bramalea Secondary', 'Khalsa Community'].map((school, i) => <div key={i} className="p-3 bg-[#0B0F19] border border-white/5" data-id="element-2980" data-mp="11oosadbpo5n6" data-mpi={i}>
                          <p className="text-[#FDFBF7] text-sm" data-id="element-2981" data-mp="rtdkkeypzyox">{school}</p>
                          <p className="text-[#D4AF37] text-xs mt-1" data-id="element-2982" data-mp="mutz0fo1gox3">
                            {Math.floor(Math.random() * 500) + 100} pts
                          </p>
                        </div>)}
                  </div>
                  <button className="w-full mt-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2983" data-mp="1q2pjnrfubbfv">
                    View All Schools
                  </button>
                </div>
              </div>
            </div>
          </div>;
      // ============================================================
      // SETTINGS
      // ============================================================
      case 'settings':
        return <div className="space-y-6" data-id="element-2984" data-mp="1cbkhahl3851u">
            <SectionHeader title="System Settings" eyebrow="Brand · economy · feature flags" action={<button className="px-4 py-2 bg-[#D4AF37] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-2986" data-mp="12clf9hutv7nt">
                  <Save className="w-3.5 h-3.5" data-id="element-2987" data-mp="2wqw59tn5s7v6" /> Save Changes
                </button>} data-id="element-2985" data-mp="1ccm2hevti7xa" />

            {/* Brand Identity */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-2988" data-mp="l4c5c0s685ig">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-2989" data-mp="h2h38u2f1o3m">
                Brand Identity
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-2990" data-mp="uudrgn0yrduq">
                Public-facing name, tagline, hero copy
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2991" data-mp="14r9vogjtxdth">
                <div data-id="element-2992" data-mp="14tmk9ck3emfi">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2993" data-mp="zsp4f6mk24h6">
                    Brand Name
                  </label>
                  <input type="text" defaultValue="Treasures of the Sikh Empire" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2994" data-mp="1drezkgptdu2w" />
                </div>
                <div data-id="element-2995" data-mp="3amuncx8p2rl4">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2996" data-mp="2lwf3lc7zcua9">
                    Tagline
                  </label>
                  <input type="text" defaultValue="Recovering the lost stories of the Sikh Kingdoms" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-2997" data-mp="2gydi6kn30ivm" />
                </div>
                <div className="md:col-span-2" data-id="element-2998" data-mp="1y7cbtbjmtl21">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-2999" data-mp="1y5j6bzkr96vm">
                    Hero CTA Label
                  </label>
                  <input type="text" defaultValue="Enter the Lahore Court" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#FDFBF7] text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-3000" data-mp="1o9pw4muidph0" />
                </div>
              </div>
            </div>

            {/* Economy */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-3001" data-mp="3v59yr9cgt7qp">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-3002" data-mp="3v3golk2n85dy">
                Economy
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-3003" data-mp="8pyzowvflf8q">
                XP values & Darbar fees
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3004" data-mp="378w4yy8hbqhj">
                {[{
                label: 'XP per Daily Packet',
                value: '50'
              }, {
                label: 'XP per Code Redeem',
                value: '200'
              }, {
                label: 'Darbar Fee (XP)',
                value: '1'
              }, {
                label: 'Referral Bonus XP',
                value: '500'
              }].map(f => <div key={f.label} data-id="element-3005" data-mp="2xg7obvbe75jz">
                    <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-3006" data-mp="2sio9ouas8qri">
                      {f.label}
                    </label>
                    <input type="number" defaultValue={f.value} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-3007" data-mp="36hnwrxincxlk" />
                  </div>)}
              </div>
            </div>

            {/* Event code prefix */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-3008" data-mp="2nlpee8fdudrq">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-3009" data-mp="2irbk69cgicon">
                Event Codes
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-3010" data-mp="1v17fdynj86w2">
                Format & generation rules
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3011" data-mp="1gxi4zy76unvn">
                <div data-id="element-3012" data-mp="1loi6lgr95u4h">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-3013" data-mp="1loqgczs2wjbg">
                    Code Prefix
                  </label>
                  <input type="text" defaultValue="COURT-" className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-3014" data-mp="16xres8pnuv1d" />
                </div>
                <div data-id="element-3015" data-mp="1pq3pbiqgx2ml">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-3016" data-mp="1pqli25dx8ypr">
                    Code Length
                  </label>
                  <input type="number" defaultValue={12} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-3017" data-mp="1uqzqhojfqaob" />
                </div>
                <div data-id="element-3018" data-mp="ziz75rxx7whm">
                  <label className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest block mb-1.5" data-id="element-3019" data-mp="upw8j5eadwuk">
                    Default Expiry (days)
                  </label>
                  <input type="number" defaultValue={30} className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 text-[#D4AF37] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[#D4AF37]" data-id="element-3020" data-mp="pp03goi784nk" />
                </div>
              </div>
            </div>

            {/* Feature flags */}
            <div className="bg-[#131B2B] border border-[#D4AF37]/20 p-5" data-id="element-3021" data-mp="18fzojemkoubk">
              <h3 className="text-[#FDFBF7] font-serif text-lg mb-1" data-id="element-3022" data-mp="13hpux8ghy1s2">
                Feature Flags
              </h3>
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-3023" data-mp="3t9mo2hpi5fq9">
                Toggle modules on or off
              </p>
              <div className="divide-y divide-[#D4AF37]/10" data-id="element-3024" data-mp="23r7kwp3w4ctl">
                {[{
                key: 'tradeMarket' as const,
                label: 'Open Trade Market',
                desc: 'Allow members to list and trade artifacts in the Open Market.'
              }, {
                key: 'courtSessions' as const,
                label: 'Court Sessions (Virtual)',
                desc: 'Enable virtual Court Session events on the Court Channel.'
              }, {
                key: 'mislSwitching' as const,
                label: 'Misl Switching',
                desc: 'Allow members to leave and join a different Misl.'
              }, {
                key: 'publicLeaderboard' as const,
                label: 'Public Leaderboard',
                desc: 'Show the global XP leaderboard to non-members.'
              }].map(f => <div key={f.key} className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-4" data-id="element-3025" data-mp="yz7jc6v4coh5">
                    <div className="flex-1 min-w-0" data-id="element-3026" data-mp="18y5cjn3hp66h">
                      <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-3027" data-mp="18zqaznsxd99m">
                        {f.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-xs mt-0.5" data-id="element-3028" data-mp="141ex91zieem4">
                        {f.desc}
                      </p>
                    </div>
                    <Toggle on={featureFlags[f.key]} onChange={() => setFeatureFlags({
                  ...featureFlags,
                  [f.key]: !featureFlags[f.key]
                })} data-id="element-3029" data-mp="1dwgqoa0ti2ui" />
                  </div>)}
              </div>
            </div>

            {/* Danger zone */}
            <div className="bg-[#131B2B] border border-red-400/30 p-5" data-id="element-3030" data-mp="1wlwsr1s5i3d1">
              <h3 className="text-red-400 font-serif text-lg mb-1" data-id="element-3031" data-mp="2ga1dowshsp2c">
                Danger Zone
              </h3>
              <p className="text-red-400/60 text-[10px] tracking-[0.2em] uppercase mb-5" data-id="element-3032" data-mp="2k90mwtl9b7zl">
                Irreversible actions
              </p>
              <div className="space-y-3" data-id="element-3033" data-mp="2b9nd1zt80hec">
                {[{
                label: 'Reset Demo Data',
                desc: 'Restore all mock data to the initial state. Cannot be undone.'
              }, {
                label: 'Archive Current Season',
                desc: 'Close out the current set of collections and start a new season.'
              }, {
                label: 'Transfer Ownership',
                desc: 'Hand over Founder Admin privileges to another account.'
              }].map(d => <div key={d.label} className="flex items-center justify-between gap-4 p-4 border border-red-400/20 bg-red-400/[0.03]" data-id="element-3034" data-mp="1il9g7ndvbr4d">
                    <div className="flex-1 min-w-0" data-id="element-3035" data-mp="1u2p1rc1v9czk">
                      <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-3036" data-mp="1p2ufxmwy7dld">
                        {d.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-xs mt-0.5" data-id="element-3037" data-mp="1k380d6tndl9e">
                        {d.desc}
                      </p>
                    </div>
                    <button className="text-red-400 hover:text-red-300 text-[10px] uppercase tracking-widest border border-red-400/40 hover:bg-red-400/10 px-3 py-2 transition-colors shrink-0" data-id="element-3038" data-mp="1aegbx8nspj38">
                      {d.label.split(' ')[0]}
                    </button>
                  </div>)}
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  // ============================================================
  // INLINE VARIANT — renders inside a parent container (no overlay)
  // ============================================================
  if (inline) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="flex flex-col lg:flex-row border border-[#D4AF37]/20 bg-[#131B2B]/30 backdrop-blur-sm min-h-[700px] w-full" data-id="element-3039" data-mp="m96zzq4d8yu7">
        {/* Admin Sidebar */}
        <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-[#D4AF37]/20 flex lg:flex-col overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto no-scrollbar shrink-0" data-id="element-3040" data-mp="3uogxsm183hw6">
          {/* Admin badge + exit */}
          <div className="hidden lg:flex items-center justify-between gap-2 px-5 py-4 border-b border-[#D4AF37]/15 bg-[#0B0F19]/50" data-id="element-3041" data-mp="dabuyhd25ush">
            <div className="flex items-center gap-2 min-w-0" data-id="element-3042" data-mp="3bz0wvxlmlnf8">
              <div className="w-7 h-7 border border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/10 shrink-0" data-id="element-3043" data-mp="3gydhscsxskf6">
                <Shield className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-3044" data-mp="1d0sjtewbdxdr" />
              </div>
              <div className="min-w-0" data-id="element-3045" data-mp="1i05g0q92sgt0">
                <p className="font-serif text-[13px] text-[#FDFBF7] leading-none truncate" data-id="element-3046" data-mp="1hz3shmavi1ue">
                  Treasury Admin
                </p>
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.2em] uppercase mt-1 truncate" data-id="element-3047" data-mp="1r1d4kglsi39w">
                  Control Center
                </p>
              </div>
            </div>
            <button onClick={onClose} aria-label="Exit admin" title="Exit admin" className="w-7 h-7 flex items-center justify-center text-[#FDFBF7]/50 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors shrink-0" data-id="element-3048" data-mp="za794dh0ktqy">
              <X className="w-3.5 h-3.5" data-id="element-3049" data-mp="bkuomhg8kz9c" />
            </button>
          </div>

          {/* Mobile exit button (inline with tab row) */}
          <button onClick={onClose} className="lg:hidden flex items-center gap-2 px-5 py-4 text-[#D4AF37] border-r border-[#D4AF37]/15 shrink-0" data-id="element-3050" data-mp="flemxocjq8x2">
            <X className="w-4 h-4" data-id="element-3051" data-mp="33ccj25xwboq" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-3052" data-mp="34dkgxrnsi28">
              Exit
            </span>
          </button>

          {tabs.map(tab => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-5 py-4 lg:py-3 text-left transition-all relative shrink-0 lg:shrink ${activeTab === tab.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5'}`} data-id="element-3053" data-mp="3q7s61c2fe2tu">
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 lg:h-auto lg:right-auto lg:top-0 lg:bottom-0 lg:w-0.5 bg-[#D4AF37]" data-id="element-3054" data-mp="37fzezn6x2ror" />}
              <tab.icon className="w-4 h-4 shrink-0" data-id="element-3055" data-mp="2xkxnikp65dj0" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-3056" data-mp="2nwa6p2ofkxnk">
                {tab.label}
              </span>
            </button>)}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-5 sm:p-8 lg:p-10 overflow-y-auto max-h-[80vh] lg:max-h-[800px]" data-id="element-3057" data-mp="2dz5thsckcciw">
          {renderContent()}
        </div>
      </motion.div>;
  }
  // ============================================================
  // OVERLAY VARIANT — original full-screen modal
  // ============================================================
  return <AnimatePresence data-id="element-3058" data-mp="2wwykpxdtqy0g">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-[#0B0F19] flex flex-col" data-id="element-3059" data-mp="2wxi29czkbkdy">
        {/* Topbar */}
        <div className="h-16 border-b border-[#D4AF37]/20 flex items-center justify-between px-6 shrink-0 bg-[#0B0F19]" data-id="element-3060" data-mp="3ucch7mtokkgm">
          <div className="flex items-center gap-4" data-id="element-3061" data-mp="1iwu2n7f6dvbt">
            <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/10" data-id="element-3062" data-mp="1ivixp49zuhj4">
              <Shield className="w-4 h-4 text-[#D4AF37]" data-id="element-3063" data-mp="1dwg6ammxs9tm" />
            </div>
            <div data-id="element-3064" data-mp="1smnca2vrc0df">
              <h1 className="font-serif text-lg text-[#FDFBF7] leading-none" data-id="element-3065" data-mp="1sp9pai1f3831">
                Treasury Admin
              </h1>
              <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.2em] uppercase mt-1" data-id="element-3066" data-mp="zy9e9n4o541v">
                System Control Center
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-3067" data-mp="v1rfd3w0rzxg">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/30 bg-[#131B2B]" data-id="element-3068" data-mp="18zz78xe78b1r">
              <div className="w-6 h-6 rounded-full bg-[#0B0F19] border border-[#D4AF37]/40 flex items-center justify-center" data-id="element-3069" data-mp="14w1agtccjcbv">
                <span className="text-[#D4AF37] text-[10px] font-serif" data-id="element-3070" data-mp="2kg7ehc4ksajr">H</span>
              </div>
              <div className="text-left" data-id="element-3071" data-mp="2gf3v4fnbjwds">
                <p className="text-[#FDFBF7] text-[11px] leading-none" data-id="element-3072" data-mp="3bnzfp2m6rgxj">
                  Harnoor S.
                </p>
                <p className="text-[#D4AF37]/60 text-[8px] tracking-widest uppercase mt-0.5" data-id="element-3073" data-mp="3fp1fplnxfx0k">
                  Founder Admin
                </p>
              </div>
            </div>
            <button onClick={onClose} aria-label="Close admin dashboard" className="w-9 h-9 flex items-center justify-center text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-3074" data-mp="3pnqlwh6gnt0z">
              <X className="w-5 h-5" data-id="element-3075" data-mp="78py9wvznj59" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-3076" data-mp="7b2d416g9de3">
          {/* Sidebar */}
          <div className="w-60 border-r border-[#D4AF37]/20 bg-[#131B2B]/50 overflow-y-auto py-4 flex flex-col gap-0.5 shrink-0" data-id="element-3077" data-mp="c9x2xn2u427p">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors relative text-left ${activeTab === tab.id ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/5'}`} data-id="element-3078" data-mp="m71qnrcbw5qm">
                {activeTab === tab.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#D4AF37]" data-id="element-3079" data-mp="1rrtn0n4phsni" />}
                <tab.icon className="w-4 h-4 shrink-0" data-id="element-3080" data-mp="2pdhb8kr0d4ny" />
                <span className="truncate" data-id="element-3081" data-mp="2kffo9oqgol77">{tab.label}</span>
              </button>)}
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-[#0B0F19]" data-id="element-3082" data-mp="2fnehgic2bmok">
            <div className="max-w-6xl mx-auto" data-id="element-3083" data-mp="2aq5ve6nsmyi1">{renderContent()}</div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
};